// ============================================================================
// @orgazone/sync-contract — LE CONTRAT de synchronisation V2 (device ↔ serveur).
// Types PURS, ZÉRO dépendance (comme les `core-*`), importés par les DEUX locuteurs : le moteur device
// `@orgazone/sync-client` ET la moitié serveur : `apps/sync` (LA porte device) et `apps/backoffice`
// (administration). ⚠️ Il n'y a PAS d'API par produit — la découpe se fait par surface d'exposition,
// pas par produit (SPEC §24.9, doctrine amendée le 2026-09-02).
// C'est *la définition* de comment transitent les infos : enveloppe, canaux + sens, delta descendant
// (pull), mutation montante (push), curseurs, codes d'erreur, endpoints. Concrétise SPEC §16.2 (enveloppe
// §16.2.7, delta par curseur + tombstones §13.2, tables `outbox`/`sync_state` §16.2.6). Réf. CADRAGE-Sync-Substrat §2.
// ⚠️ Ce package ne CONTIENT aucune logique réseau/DB : il est le LANGAGE, pas les locuteurs.
// ============================================================================

// ── Enveloppe unifiée (SPEC §16.2.7) — SOURCE UNIQUE : `@orgazone/api-envelope` ───────────────
// ⚠️ ZÉRO DÉRIVE (règle utilisateur 2026-08-01, bêta = pas de V1/V2) : l'enveloppe n'est PAS redéfinie ici,
// elle est ré-exportée du package plateforme pour que TOUTES les API (console, BO, store, edi…) partagent
// exactement le même type et les mêmes fabriques. Une seule définition = rien à resynchroniser.
import type { EnvelopeError } from '@orgazone/api-envelope';
// ⚠️ `failDetail` est ré-exportée ici comme `fail` : un service qui importerait le motif d'échec depuis
// un second paquet ferait cohabiter deux provenances pour la même enveloppe — c'est exactement ce que la
// source unique existe pour empêcher.
export { HTTP, MESSAGES, ok, fail, failDetail, isEnvelope, isMessageKey, defaultMessageFor } from '@orgazone/api-envelope';
export type { Envelope, EnvelopeError, EnvelopeStatus } from '@orgazone/api-envelope';

// ── Canaux + gouvernance de sens (§6.7 / §12.9 / §16.2.6) ────────────────────────────────────
// down = BO→device (le device ne le modifie pas) · up = device→BO (append-only, ex. fiscal) ·
// bi = bidirectionnel arbitré SERVEUR (last-write-wins), journalisé (§6.7).
export type SyncDirection = 'down' | 'up' | 'bi';

/** Enfant d'un AGRÉGAT : table fille + colonne qui pointe la racine. */
export interface AggregateChild {
  entity: string;
  /** Colonne portant l'identifiant de la RACINE (jointure de l'agrégat). */
  fk: string;
  /**
   * Colonne d'IDENTITÉ de l'enfant — défaut `'id'`.
   * ⚠️ Toutes les tables enfants n'ont pas `id` pour clé : `client_wallet` est identifiée par
   * `client_id` (relation 1-1 avec la fiche). Le device en a besoin pour écrire son upsert :
   * un `ON CONFLICT (id)` sur une table sans colonne `id` est REFUSÉ par SQLite dès la préparation
   * de la requête — donc systématiquement, même quand aucun conflit n'existe. Symptôme constaté en
   * bêta 2026-08-05 : « ON CONFLICT clause does not match any PRIMARY KEY or UNIQUE constraint » à
   * chaque client porteur d'un wallet. La clé appartient au CONTRAT, pas à une supposition du client.
   */
  key?: string;
}

export interface ChannelDef {
  name: string;        // identifiant du canal (== clé du registre)
  direction: SyncDirection;
  entity: string;      // table RACINE (db-schema)
  key: string;         // clé métier d'arbitrage LWW (souvent 'id')
  /** ⚠️ Canal `up` dont l'entité ÉVOLUE côté device (statuts de session/retouche…) : la direction dit QUI
   *  écrit, pas l'immuabilité. Sans ce flag, la projection serveur (INSERT … DO NOTHING) figèrerait l'entité
   *  à son premier état — une session cloturée resterait « ouverte » au BO pour toujours. */
  mutable?: true;
  /** ⚠️ Canal `down` dont le CONTENU est immuable (pièces SIGNÉES redescendues) : le device l'applique en
   *  INSERT-SEULEMENT — ses gardes NF525 locales interdisent UPDATE/DELETE sur les tables signées, et une
   *  pièce signée ne change de toute façon jamais. Sans ce drapeau, l'application ferait un upsert (et un
   *  vider/réinsérer des enfants d'agrégat) que la garde ferait échouer — à raison. */
  appendOnly?: true;
  /**
   * ⚠️ CE CANAL NE SE REJOUE PAS À LA RECONSTRUCTION, et c'est un INTERDIT, pas un oubli.
   *
   * La reconstruction montante (« la flotte reconstruit le cloud ») renvoie l'historique local au
   * serveur. Pour une pièce fiscale, c'est exactement ce qu'on veut. Pour une DEMANDE D'ENVOI de
   * courrier, ce serait renvoyer à des clients des tickets qu'ils ont déjà reçus — un effet dans le
   * MONDE, que ni l'idempotence ni les ids déterministes ne rattrapent : le courrier est parti.
   */
  rebuild?: false;
  /**
   * ⚠️ 🔴 L'EXPÉDITEUR POSSÈDE L'ISSUE — canal `bi` où le dernier écrivain NE gagne PAS.
   *
   * Le siège décide de la DEMANDE, l'appareil décide de ce qui s'est PASSÉ (parti, refusé, combien de
   * tentatives) parce que c'est lui qui agit sur le monde. Une descente en dernier-écrivain-gagne
   * rendrait à une ligne déjà partie son état « en attente », et l'appareil **referait l'envoi** : le
   * client recevrait deux fois son ticket, et aucune idempotence ne rattrape un courrier posté.
   *
   * ⚠️ L'APPAREIL APPLIQUE DONC LA DESCENTE EN INSERT-SI-ABSENT : une demande NEUVE entre (c'est ainsi
   * que le siège demande un renvoi), une demande qu'il connaît déjà est ignorée. Un renvoi est une
   * demande NEUVE, jamais la remise à zéro d'une ancienne.
   *
   * ⚠️ À NE PAS CONFONDRE AVEC `appendOnly`, qui dit que le CONTENU est immuable (pièce signée). Ici
   * le contenu change tout le temps — ce qui est en jeu est QUI a le droit de l'écrire.
   */
  senderOwned?: true;
  /** ⚠️ AGRÉGAT ATOMIQUE [B1] : la racine et ses enfants voyagent dans UNE mutation et se projettent dans
   *  UNE transaction. Motif : une pièce fiscale sans ses lignes / règlements / TVA **n'est pas une pièce**
   *  (NF525) — remonter la racine seule publierait un état faux. Réf. CADRAGE-Reconstruction §2.2. */
  aggregate?: readonly AggregateChild[];
  /** ⚠️ Canal DÉCLARÉ EN AVANCE : le canal existe au contrat, sa TABLE n'est pas encore déployée. Son
   *  absence en base est un état ATTENDU, pas une panne — sans ce drapeau, l'amorçage d'un magasin
   *  rendait `ok: false` à CHAQUE mise en service et à chaque reprise (constaté 2026-08-05). Une alerte
   *  qui se déclenche toujours n'est plus une alerte : on apprend à l'ignorer, et la vraie passe. */
  pending?: true;
  /**
   * ⚠️ CANAL FILTRÉ PAR DISPOSITION : la colonne qui porte la disposition d'articles rapides d'une ligne. Une caisse
   * ne reçoit que les lignes de SA disposition, et celles des autres lui arrivent en SUPPRESSION — sautées, une tuile
   * déplacée ailleurs resterait affichée pour toujours sur la caisse qui l'avait.
   */
  layoutKey?: string;
}

/** Charge utile d'un canal AGRÉGAT : la ligne racine + ses lignes filles par table. */
export interface AggregatePayload {
  root: Record<string, unknown>;
  children: Record<string, Record<string, unknown>[]>;
}

/** Vrai si la charge respecte la forme d'agrégat (garde-fou avant projection). */
export function isAggregatePayload(v: unknown): v is AggregatePayload {
  if (typeof v !== 'object' || v === null) return false;
  const p = v as AggregatePayload;
  return typeof p.root === 'object' && p.root !== null && typeof p.children === 'object' && p.children !== null;
}

// Le registre est la SOURCE UNIQUE de « quelle entité se synchronise et dans quel sens ».
// ⚠️ `settings.key = 'name'` : la colonne d'identité de `setting` est `name` (db-schema), pas `key`
// (le cadrage l'écrivait « key » de façon générique — on aligne sur le schéma réel).
// ⚠️ REGISTRE COMPLET [B3] (2026-08-01). L'ancienne version ne déclarait que 3 entités montantes sur les
// 47 tables de la base magasin : le JET, les grands totaux, la caisse, les duplicata et les ventes abandonnées
// n'avaient AUCUN canal, et une pièce fiscale serait remontée AMPUTÉE de ses lignes/règlements/TVA. Ce registre
// est la matrice de couverture du CADRAGE-Reconstruction-Bidirectionnelle §3 — il sert la sync normale ET la
// reconstruction dans les deux sens.
// ⚠️ NE FIGURENT PAS ICI, volontairement :
//   · la clé PRIVÉE de signature — elle ne quitte JAMAIS le Keystore de l'appareil, et elle ne le PEUT
//     pas : la clé est générée non exportable. ⚠️ CETTE EXCLUSION A LONGTEMPS PORTÉ SUR TOUTE LA
//     TABLE `fiscal_key`, ce qui était le mauvais objet — voir le canal montant du même nom ci-dessous ;
//   · `daily_stat` — DÉRIVÉ (recalculé depuis les pièces) : le transporter créerait une seconde vérité ;
//     (⚠️ `stock` FIGURAIT ICI pour la même raison, et l'exclusion a été LEVÉE le 2026-08-13 — voir le
//      canal `stock` ci-dessous, qui explique pourquoi le motif se retournait contre lui-même) ;
//   · `fiscal_sequence`, `business_sequence` — RE-DÉRIVÉS à la reconstruction [B7] (un compteur transporté peut
//     régresser et casser la continuité NF525) ;
//   · `outbox`, `sync_state` — mécanique de sync propre au device ;
//   · `fiscal_floor` — le plancher des grands totaux d'une caisse reprise : la caisse le TIENT du serveur
//     (`/sync/register-counters`), il n'a rien à lui renvoyer ;
//   · `cash_floor` — le tiroir d'une caisse reprise (fond laissé, chèques en attente), même source, même raison ;
//   · `payment_intent` — transitoire (intention de paiement en cours de dialogue avec le TPE) ;
//   · `cash_variance_note` — ÉCRITE PAR LE BACK-OFFICE dans la base du magasin, jamais par la caisse.
//     Elle justifie un écart de comptage (gestion, hors NF525) et n'a aucun lecteur au comptoir : lui
//     ouvrir un canal descendant ferait voyager une donnée que personne n'y regarde.
//     (⚠️ `held_sale` FIGURAIT ICI pour la même raison, et l'exclusion a été LEVÉE le 2026-08-19 :
//      « transitoire » décrivait la donnée, pas son usage. Le back-office doit MONTRER les paniers
//      suspendus — c'est de l'argent qui n'est pas encaissé et qui ne passera pas la clôture sans
//      décision. Voir le canal `held_sale` ci-dessous.)
/**
 * FENÊTRE de l'historique fiscal INTER-CAISSES (§16.2.8septies) — 6 mois (décision utilisateur 2026-08).
 *
 * ⚠️ Elle arbitre entre « pouvoir rembourser/dupliquer un achat ancien fait à la caisse voisine » et
 * « ne pas faire porter le magasin ENTIER à chaque tablette » (sur la base de référence : ~3 000 pièces
 * par semestre pour tout le magasin, contre 24 000+ en 4 ans). Elle se mesure sur `occurredAt` — l'heure
 * MÉTIER de la vente — jamais sur l'heure d'enregistrement serveur : « les ventes des 6 derniers mois »
 * est une phrase sur le commerce, pas sur la réplication.
 * ⚠️ La RÉTENTION du journal doit la respecter (retention.ts) : purger `fiscal_piece` plus tôt rendrait
 * la fenêtre indescendable pour toute caisse ajoutée après la purge.
 */
export const FISCAL_HISTORY_WINDOW_MS = 183 * 86_400_000; // ~6 mois

export const CHANNELS = {
  // ── DESCENDANTS (BO → device) : le device les lit, ne les modifie jamais ────────────────────
  catalog:         { name: 'catalog',         direction: 'down', entity: 'article',           key: 'id' },
  article_photo:   { name: 'article_photo',   direction: 'down', entity: 'article_photo',     key: 'id' },
  // ⚠️ Ce canal porte AUSSI le compte d'administration éditeur (`operator.id = 'ADMIN'`, §26.4bis) : il
  // est décidé par la Console pour tout le parc, relayé par le back-office, et arrive ici comme
  // n'importe quel opérateur. Pas de canal ni de table dédiés pour une seule ligne — ce qui protège cet
  // identifiant est une GARDE d'écriture côté serveur, pas une séparation de plomberie.
  operators:       { name: 'operators',       direction: 'down', entity: 'operator',          key: 'id' },
  payment_method:  { name: 'payment_method',  direction: 'down', entity: 'payment_method',    key: 'id' },
  reference_list:  { name: 'reference_list',  direction: 'down', entity: 'reference_list',    key: 'id' },
  justification:   { name: 'justification',   direction: 'down', entity: 'justification',     key: 'id' },
  // ⚠️ LE STUDIO DES ARTICLES RAPIDES : l'arbre de tuiles d'une disposition, et ce que la caisse doit savoir pour le
  //    dessiner — la SEULE forme des articles rapides (la liste à plat d'avant le studio n'existe plus).
  quick_tile:           { name: 'quick_tile',           direction: 'down', entity: 'quick_tile',           key: 'id', layoutKey: 'layout_id' },
  quick_layout_display: { name: 'quick_layout_display', direction: 'down', entity: 'quick_layout_display', key: 'id', layoutKey: 'id' },
  promotion:       { name: 'promotion',       direction: 'down', entity: 'promotion',         key: 'id' },
  promotion_target:{ name: 'promotion_target',direction: 'down', entity: 'promotion_target',  key: 'id' },
  size_grid:       { name: 'size_grid',       direction: 'down', entity: 'size_grid',         key: 'id' },
  // LES VALEURS DES GRILLES, UNE LIGNE CHACUNE : c'est ce que `article.size_id` pointe. Elles voyagent
  // avec la grille (même bouton de récupération), et c'est par leur `rank` que la caisse rangera ses
  // matrices une fois qu'elle les lira — en attendant, `size_grid.sizes` en reste la projection.
  size:            { name: 'size',            direction: 'down', entity: 'size',              key: 'id' },
  // LES GRILLES D'UNE FAMILLE — écrites avec la famille, une ligne par lien ; une caisse les reçoit dès qu'elle en
  // déclare la table (la porte des canaux déclarés), un APK antérieur n'en voit rien.
  family_size_grid:{ name: 'family_size_grid',direction: 'down', entity: 'family_size_grid',  key: 'id' },
  // ⚠️ LE SECOND AXE N'A PAS DE CANAL À PART : une grille secondaire (longueur, bonnet) est une ligne de
  // `size_grid` comme les autres (`axis`), ses valeurs des lignes de `size` — elles voyagent par les deux
  // canaux ci-dessus. Le canal `size_grid2` a existé le temps de la bascule ; aucun APK ne l'a porté.
  // ⚠️ La CORRESPONDANCE des graphies fournisseur (`size_dimension_alias`) n'a PAS de canal non plus :
  // elle traduit pendant une reprise ou un import, au serveur — une caisse ne traduit rien.
  // STRUCTURE MARCHANDE (rayon → famille → sous-famille) et COLORIS. Descendants : le back-office en est
  // maître, la caisse les LIT pour afficher et filtrer. ⚠️ Trois canaux distincts et non un seul : ils
  // évoluent à des rythmes très différents — 1 729 coloris brassés par chaque catalogue fournisseur contre
  // une poignée de rayons réorganisés une fois l'an. Un canal commun ferait retirer tout le reste à chaque
  // import, et la caisse rejouerait des rayons qui n'ont pas bougé.
  sector:          { name: 'sector',          direction: 'down', entity: 'sector',            key: 'id' },
  family:          { name: 'family',          direction: 'down', entity: 'family',            key: 'id' },
  sub_family:      { name: 'sub_family',      direction: 'down', entity: 'sub_family',        key: 'id' },
  collection:      { name: 'collection',      direction: 'down', entity: 'collection',        key: 'id' },
  color:           { name: 'color',           direction: 'down', entity: 'color',             key: 'id' },
  // FOURNISSEUR et MARQUE — deux canaux et non un seul, pour la même raison que la structure marchande :
  // les marques bougent au rythme des collections, les fournisseurs à celui des contrats. Un canal
  // commun ferait rejouer les seconds à chaque mouvement des premières.
  supplier:        { name: 'supplier',        direction: 'down', entity: 'supplier',          key: 'id' },
  brand:           { name: 'brand',           direction: 'down', entity: 'brand',             key: 'id' },
  insee_city:      { name: 'insee_city',      direction: 'down', entity: 'insee_city',        key: 'id' },
  // PHOTO DE STOCK (2026-08-13) — la quantité par variante, calculée par le SERVEUR et seulement par lui.
  //
  // ⚠️ CE CANAL A ÉTÉ EXCLU, puis rétabli. Le motif d'exclusion — « dérivé, le transporter créerait une
  // seconde vérité » — se retournait contre lui-même : `stock_movement` est `up`, donc une caisse ne
  // reçoit PAS les mouvements des autres. Sur un magasin à 15 caisses, chacune dérivait son total depuis
  // son quinzième de l'information. Il n'y avait pas une vérité, il y en avait quinze.
  //
  // ⚠️ CE N'EST PAS UNE SECONDE VÉRITÉ, parce que la caisse ne la CALCULE PAS : elle la LIT. Le serveur
  // consolide (`substrate/consolidate-stock.ts`) et journalise la photo ; les caisses la reçoivent datée
  // d'un `rev`. C'est un relevé, pas un second calcul.
  //
  // ⚠️ CE QUI NE DESCEND JAMAIS : le grand livre `stock_movement` (~100 000 lignes sur 4 ans, 26 Mo, et il
  // ne converge pas). La photo est bornée par le catalogue : 15 625 lignes / 2,9 Mo sur le magasin de
  // référence, soit sept fois moins que le catalogue déjà descendu.
  //
  // ⚠️ `down` ET NON `bi`, alors que la caisse écrit son stock : elle le fait par MOUVEMENTS (canal `up`),
  // jamais en poussant un total. Un total poussé par quinze caisses serait quinze écrasements successifs.
  stock:           { name: 'stock',           direction: 'down', entity: 'stock',             key: 'id' },
  // KILL-SWITCH (§13.1bis) — les consignes de blocage posées à la Console. ⚠️ `down` STRICT et table
  // DÉDIÉE : porter ces consignes par le canal `settings` (qui est `bi`) aurait permis à la caisse de
  // repousser — donc d'effacer — son propre verrou (audit 2026-08 §17). Une cible ne peut pas être
  // l'écrivain du contrôle qui la vise. La LEVÉE d'un blocage voyage en tombstone (`delete`).
  blockade:        { name: 'blockade',        direction: 'down', entity: 'device_blockade',   key: 'id' },
  objectives:      { name: 'objectives',      direction: 'down', entity: 'objective',         key: 'id', pending: true }, // §834 — table À VENIR
  // LA DÉCISION PRISE SUR UN BROUILLON (§11.6) — validé, fusionné, refusé.
  //
  // ⚠️ CANAL SÉPARÉ ET `down` STRICT, alors qu'il porte sur la MÊME entité métier que `article_draft`.
  // Un seul canal `bi` aurait laissé la caisse repousser son brouillon PAR-DESSUS le refus qui le
  // vise, et l'effacer sans que personne ne le voie. Deux écrivains, deux canaux : la caisse écrit ce
  // qu'elle a créé, le back-office écrit ce qu'il en a décidé. Même règle que `blockade`.
  //
  // ⚠️ Il DOIT redescendre : sans lui, une caisse continuerait d'offrir un article refusé, et
  // l'opérateur le recréerait au prochain client.
  article_draft_decision: { name: 'article_draft_decision', direction: 'down', entity: 'article_draft_decision', key: 'id' },
  // HISTORIQUE FISCAL DESCENDANT (§16.2.8septies, cadrage arbitré 2026-08-12/13) — les pièces des AUTRES
  // caisses du magasin, pour le quotidien multi-caisses : retour, échange, duplicata d'un ticket émis au
  // comptoir voisin — et le DÉJÀ-RETOURNÉ compté sur tout le magasin (sans lui, le même article se
  // retournerait à chaque caisse).
  //
  // ⚠️ CE CANAL N'A PAS D'ÉVÉNEMENTS PROPRES : le serveur RE-SERT ceux du canal `fiscal_piece` (montant)
  // en EXCLUANT le registre du demandeur et en BORNANT à FISCAL_HISTORY_WINDOW_MS. Dupliquer chaque vente
  // dans un second événement doublerait le stockage fiscal du journal pour un contenu identique.
  //
  // ⚠️ `down` STRICT, jamais `bi` : poussable, il permettrait à une caisse d'écrire dans la chaîne d'une
  // autre. L'autre moitié de la garde vit côté device (§B1) : toute pièce entrante portant le registre
  // LOCAL est refusée — les deux bouts se défendent, aucun ne fait confiance à l'autre.
  //
  // ⚠️ MÊME AGRÉGAT que `fiscal_piece` : sans règlements ni ventilation TVA, un duplicata est impossible —
  // le §6.3.1.1 exige une restitution « strictement identique ». `appendOnly` : pièces SIGNÉES, le device
  // les écrit en INSERT-SEULEMENT (ses gardes NF525 interdisent UPDATE/DELETE, et c'est très bien ainsi).
  //
  // ⚠️ La caisse peut LIRE ces signatures, pas les VÉRIFIER (la clé privée ne quitte jamais le Keystore
  // de l'émetteur) : aucun écran ne doit promettre une vérification qu'il ne peut pas faire (§23.0).
  fiscal_history:  { name: 'fiscal_history',  direction: 'down', entity: 'fiscal_piece',      key: 'id', appendOnly: true,
    aggregate: [
      { entity: 'fiscal_piece_line',    fk: 'piece_id' },
      { entity: 'fiscal_piece_payment', fk: 'piece_id' },
      { entity: 'fiscal_piece_tax',     fk: 'piece_id' },
    ] },
  // LES DUPLICATA DES AUTRES CAISSES (§6.3.1.2) — le rang « Duplicata n°X » est celui du TICKET, pas de la caisse qui
  // le réimprime. Sans ce canal, chaque caisse comptait ses réimpressions seule : deux caisses scellaient
  // « Duplicata n°1 » pour le même ticket, et une tablette de remplacement repartait de zéro.
  //
  // ⚠️ MÊMES FILTRES QUE `fiscal_history`, et le même service : jamais le registre du demandeur (sa chaîne de
  // duplicata est signée ici), sauf à la reconstruction, où il redescend sous `RECOVERY-R<n>` ; la même fenêtre,
  // puisqu'on ne réimprime qu'une pièce qu'on détient. `down` STRICT et `appendOnly` : un duplicata est signé.
  // ⚠️ PAS D'UNICITÉ AU SIÈGE sur le rang : deux caisses hors ligne peuvent encore sceller le même rang, et refuser le
  // second ferait disparaître du siège — donc de l'archive fiscale — un duplicata scellé et imprimé.
  reprint_history: { name: 'reprint_history', direction: 'down', entity: 'fiscal_reprint',    key: 'id', appendOnly: true },

  // ── BIDIRECTIONNELS (arbitrage serveur last-write-wins, §6.7) ───────────────────────────────
  // ⚠️ Ce sont EUX qui rendent la reconstruction montante dangereuse (la tablette écraserait le serveur) :
  // le mode `full` exige une cible vide vérifiée SERVEUR. Cf. CADRAGE-Reconstruction §5.2.
  settings:        { name: 'settings',        direction: 'bi',   entity: 'setting',           key: 'name' },
  clients:         { name: 'clients',         direction: 'bi',   entity: 'client',            key: 'id',
    aggregate: [ // la fiche et ses satellites voyagent ensemble (adresses/contacts/wallet/qualifications)
      { entity: 'client_address', fk: 'client_id' },
      { entity: 'client_contact', fk: 'client_id' },
      { entity: 'client_wallet',  fk: 'client_id', key: 'client_id' }, // 1-1 : identifiée PAR la fiche
      // ⚠️ N↔N : un client porte ZÉRO, UNE ou PLUSIEURS qualifications. Sans cette ligne, le
      //    projecteur ne les écrirait JAMAIS — l'écriture partirait sans erreur et la fiche
      //    resterait muette (constaté au banc avant livraison, 2026-08-31).
      { entity: 'client_qualification', fk: 'client_id' },
    ] },
  credit_note:     { name: 'credit_note',     direction: 'bi',   entity: 'credit_note',       key: 'id' },
  // LES PÉRIPHÉRIQUES — TPE et imprimantes, réglables des DEUX côtés (décision utilisateur).
  //
  // ⚠️ `bi` ET NON `down`, ET C'EST LE CŒUR DE LA DÉCISION : aucun des deux côtés ne peut tout faire.
  // Une adresse IP se découvre depuis un poste du réseau — un outil de détection la donne au siège ;
  // une imprimante EMBARQUÉE dans l'équipement ne se teste que sur la tablette qui la porte. Laisser
  // le canal descendant obligeait à choisir un maître, et le produit avait choisi le pire des deux :
  // il PROMETTAIT la remontée à neuf endroits (écran Magasin du BO, huit textes de la caisse dont un
  // toast au caissier) sans qu'une seule ligne ne la réalise — la table était écrite par personne et
  // lue par personne (§23.0).
  //
  // ⚠️ IL NE SUIT PAS `blockade`, ET LA DIFFÉRENCE EST LE SENS DU GESTE. Le kill-switch est `down`
  // STRICT parce qu'« une cible ne peut pas être l'écrivain du contrôle qui la vise ». Ici personne
  // ne vise personne : les deux côtés DÉCRIVENT le même matériel, chacun avec ce qu'il est seul à
  // pouvoir constater. C'est exactement le cas que `bi` sert — arbitré au serveur, dernier écrivain.
  //
  // ⚠️ ET IL NE PASSE PAS PAR `settings`, ALORS QUE LES RÉGLAGES DE TPE Y VIVENT AUJOURD'HUI : la table
  // `setting` n'a que `name` pour clé — ni magasin, ni caisse — donc une valeur « partagée » vaudrait
  // pour TOUTES les caisses à la fois, et la caisse 2 dialoguerait avec le terminal du comptoir 1.
  // C'est ce que le catalogue des réglages nomme, et c'est pourquoi ces clés y sont `device_local`.
  // La table `peripheral` a de vraies LIGNES : deux TPE sont deux lignes, pas une valeur écrasée.
  peripheral:      { name: 'peripheral',      direction: 'bi',   entity: 'peripheral',        key: 'id' },

  // ── MONTANTS (device → BO) : append-only, jamais réécrits par le serveur ────────────────────
  fiscal_piece:    { name: 'fiscal_piece',    direction: 'up',   entity: 'fiscal_piece',      key: 'id',
    aggregate: [ // [B1] — une pièce SANS ses lignes/règlements/TVA n'est pas une pièce (NF525)
      { entity: 'fiscal_piece_line',    fk: 'piece_id' },
      { entity: 'fiscal_piece_payment', fk: 'piece_id' },
      { entity: 'fiscal_piece_tax',     fk: 'piece_id' },
    ] },
  fiscal_jet:      { name: 'fiscal_jet',      direction: 'up',   entity: 'fiscal_jet',        key: 'id' },
  fiscal_grand_total: { name: 'fiscal_grand_total', direction: 'up', entity: 'fiscal_grand_total', key: 'id' },
  fiscal_reprint:  { name: 'fiscal_reprint',  direction: 'up',   entity: 'fiscal_reprint',    key: 'id' }, // duplicata §6.3.1.2
  /**
   * LA CLÉ PUBLIQUE DE SIGNATURE — sans elle, une signature n'est vérifiable par PERSONNE.
   *
   * ⚠️ SEULE LA CLÉ PUBLIQUE VOYAGE, et c'est tout l'objet de ce canal. La clé PRIVÉE est générée
   * NON EXPORTABLE dans le Keystore : elle ne peut pas sortir, même si on le voulait. La publique, elle,
   * EST FAITE POUR SORTIR — NF525 §6.15.1 : l'éditeur doit la mettre à disposition de l'audit.
   *
   * 🔴 CE QUE SON ABSENCE COÛTAIT, MESURÉ : une base magasin réelle portait 10 675 pièces citant
   * 3 `key_id`, et ZERO clé publique. Personne au siège ne pouvait vérifier une seule signature — et
   * une tablette de remplacement ne pourra jamais vérifier son propre historique repris, la clé qui
   * l'a signé étant morte avec l'appareil perdu. C'est précisément le scénario de la reconstruction.
   *
   * ⚠️ MONTANT SEULEMENT, jamais `bi` : une clé qui redescendrait pourrait être REMPLACéE par le
   * serveur, donc une signature vérifiée contre une clé qu'il aurait choisie. La caisse est la seule
   * source de ce qu'elle a produit.
   *
   * ⚠️ PAS `appendOnly` : une rotation RETIRE une clé (`status`), sans jamais en réécrire l'empreinte
   * ni la valeur — c'est un upsert par `key_id`, idempotent par construction.
   */
  fiscal_key:      { name: 'fiscal_key',      direction: 'up',   entity: 'fiscal_key',        key: 'key_id' },
  /**
   * DEMANDE D'EFFACEMENT RGPD — la caisse la REÇOIT, le siège l'EXÉCUTE.
   * ⚠️ MONTANT SEULEMENT : le résultat redescend par la fiche client (elle devient anonymisée), jamais
   * par ce canal — une demande ne doit pas pouvoir être effacée par une descente.
   */
  client_erasure_request: { name: 'client_erasure_request', direction: 'up', entity: 'client_erasure_request', key: 'id' },
  abandoned_sale:  { name: 'abandoned_sale',  direction: 'up',   entity: 'abandoned_sale',    key: 'id',
    aggregate: [{ entity: 'abandoned_sale_line', fk: 'abandoned_sale_id' }] },                              // anti-fraude §21bis
  /**
   * VENTES EN ATTENTE — paniers suspendus, visibles au back-office (§25, onglet du journal).
   *
   * ⚠️ NON FISCAL, ET C'EST TOUT L'ENJEU : une vente en attente n'a ni numéro ni signature, elle vit
   * sur sa caisse. Le back-office la MONTRE, il n'y touche pas. La remonter ne la transforme pas en
   * pièce — elle n'entre dans aucun total, aucun chiffre d'affaires, aucun JET.
   * ⚠️ `mutable` ET TOMBSTONÉ : un panier grossit tant qu'il attend, puis DISPARAÎT quand il est
   * repris ou abandonné. Sans le `delete`, le back-office afficherait éternellement des paniers
   * encaissés depuis longtemps — un écran qui invente de l'argent en attente (§23.0).
   */
  /**
   * COURRIERS DEMANDÉS PAR LA CAISSE — le ticket, l'avoir ou la facture qu'un opérateur envoie.
   *
   * ⚠️ 🔴 C'EST LA CAISSE QUI ENVOIE (décision utilisateur) : le courrier part du MAGASIN, pas du
   * siège, qui a déjà assez à faire. Le canal fait REMONTER ce que la caisse a envoyé, pour que le
   * back-office sache ce qui est parti — et il DESCEND les renvois que le siège demande.
   *
   * ⚠️ MUTABLE, ET C'EST UNE CONSÉQUENCE DIRECTE : la ligne change après coup (envoyé, échoué, nombre
   * de tentatives). En append-only, la file resterait éternellement « en attente » au siège.
   *
   * ⚠️ 🔴 `bi` NE VEUT PAS DIRE « DERNIER ÉCRIVAIN GAGNE » ICI, et c'est la règle qui rend le canal
   * sûr : **l'EXPÉDITEUR possède l'ISSUE**. Le siège décide de la DEMANDE (quoi, à qui) ; l'appareil
   * décide de ce qui s'est PASSÉ (`sent_at`, `failed_reason`, `attempts`), puisque c'est lui qui parle
   * au serveur de courrier. Une descente qui écraserait une ligne déjà partie remettrait `sent_at` à
   * vide et **le client recevrait son ticket deux fois**. La descente n'INSÈRE donc que ce qu'elle
   * apporte de NOUVEAU, et ne réécrit jamais une demande que l'appareil connaît déjà
   * (`isSenderOwnedChannel`, appliqué par le magasin local).
   *
   * ⚠️ UN RENVOI EST UNE DEMANDE NEUVE, jamais la remise à zéro d'une ancienne : le journal garde
   * alors trace des deux envois, et aucune course n'oppose les deux copies d'une même ligne.
   *
   * ⚠️ C'EST UNE FILE, PAS UN APPEL : une réserve hors ligne ne joint aucun serveur de courrier. La
   * demande attend le réseau, puis part — exactement comme une vente attend sa remontée.
   *
   * ⚠️ AUCUN SECRET NE VOYAGE ICI : le mot de passe du serveur de courrier reste sur l'appareil qui
   * s'en sert, et ne monte JAMAIS par ce canal ni par les réglages synchronisés.
   */
  mail_request:    { name: 'mail_request',    direction: 'bi',   entity: 'mail_request',      key: 'id', mutable: true, rebuild: false, senderOwned: true },
  held_sale:       { name: 'held_sale',       direction: 'up',   entity: 'held_sale',         key: 'id', mutable: true },
  cash_session:    { name: 'cash_session',    direction: 'up',   entity: 'cash_session',      key: 'id', mutable: true,
    aggregate: [ // la session de caisse et son contenu (comptages, mouvements) sont indissociables
      { entity: 'cash_count',    fk: 'session_id' },
      { entity: 'cash_movement', fk: 'session_id' },
    ] },
  bank_deposit:    { name: 'bank_deposit',    direction: 'up',   entity: 'bank_deposit',      key: 'id' },
  stock_movement:  { name: 'stock_movement',  direction: 'up',   entity: 'stock_movement',    key: 'id' }, // §13.2rev / core-inventory
  loyalty_ledger:  { name: 'loyalty_ledger',  direction: 'up',   entity: 'loyalty_ledger',    key: 'id' },
  /**
   * GRAND LIVRE DES TITRES — les mouvements d'un bon (consommé, rendu), jamais son solde.
   *
   * ⚠️ 🔴 C'EST CE CANAL QUI REMPLACE L'INSTANTANÉ DU TITRE. La caisse poussait `credit_note` entier
   * après chaque consommation : deux caisses qui consomment le même bon avant de s'être synchronisées
   * produisaient deux instantanés, et le DERNIER PROJETÉ gagnait — une consommation disparaissait, le
   * bon affichait plus de solde qu'il n'en restait, et le client dépensait deux fois le même argent.
   * Deux caisses posent désormais deux LIGNES, qui s'ADDITIONNENT.
   *
   * ⚠️ APPEND-ONLY (`up` non mutable), et c'est ce qui le rend sûr : un mouvement ne se corrige pas,
   * il se compense par un mouvement inverse. Une annulation RESTITUE — « consommé puis rendu » n'est
   * pas « jamais consommé », et un contrôle lit la différence.
   *
   * ⚠️ LE SOLDE EST UNE PROJECTION que le serveur re-dérive (`core-vouchers.deriveFromLedger`), comme
   * il le fait déjà pour la cagnotte. Le pousser créerait une seconde vérité.
   *
   * ⚠️ IL PORTE AUSSI LA SEULE TRACE DES REMISES : un titre présenté en RÈGLEMENT laisse une ligne
   * fiscale signée (`fiscal_piece_payment.ref`), un bon de REMISE n'en laisse AUCUNE — son code
   * entrait dans le scellement, servait à décrémenter, puis disparaissait.
   */
  credit_note_ledger: { name: 'credit_note_ledger', direction: 'up', entity: 'credit_note_ledger', key: 'id' },

  /**
   * LE GRAND LIVRE DE FIDÉLITÉ QUI **REDESCEND** — le pendant de `loyalty_ledger`, dans l'autre sens.
   *
   * 🔴 POURQUOI IL EXISTE (mesuré sur une base de production). La cagnotte est tenue des DEUX côtés,
   * chacun projetant SON grand livre. La règle est bonne, mais elle suppose que les deux bouts voient
   * le MÊME grand livre — et rien ne le garantissait : `loyalty_ledger` ne monte que, et le solde est
   * retiré à la descente. Une ligne écrite par la REPRISE, ou par une AUTRE caisse, n'atteignait donc
   * jamais le comptoir. Relevé sur un seul magasin : **2 172 clients, 11 637 € de cagnotte au siège,
   * dont la caisse ignorait tout**. Le client présentait sa carte, la caisse annonçait zéro, et son
   * portefeuille lui affichait le vrai montant.
   *
   * ⚠️ MÊME PATRON QUE `fiscal_history` : un canal DESCENDANT sur la MÊME entité qu'un canal montant.
   * Le montant garde son sens (« une caisse a poussé ceci »), le descendant sert ce que le magasin
   * détient — reprise comprise, puisque l'amorçage lit les TABLES.
   *
   * ⚠️ `appendOnly` : une écriture de fidélité ne se réécrit ni ne s'efface. La caisse l'INSÈRE, point.
   * Deux exemplaires de la même ligne portent le même `id` et se dédoublonnent d'eux-mêmes : c'est ce
   * qui rend inoffensif le fait qu'une caisse reçoive en retour ses propres mouvements.
   *
   * ⚠️ AUCUNE FENÊTRE DE TEMPS, contrairement à l'historique fiscal — et c'est la différence qui
   * compte. Tronquer un historique de VENTES ampute une consultation ; tronquer un grand livre de
   * fidélité ampute un SOLDE, c'est-à-dire de l'argent dû au client. On sert tout, ou on ment.
   */
  loyalty_history: { name: 'loyalty_history', direction: 'down', entity: 'loyalty_ledger',    key: 'id', appendOnly: true },
  loyalty_card_history: { name: 'loyalty_card_history', direction: 'up', entity: 'loyalty_card_history', key: 'id', mutable: true }, // is_used/used_at évoluent
  service:         { name: 'service',         direction: 'up',   entity: 'service',           key: 'id', mutable: true }, // retouches §17 (statuts évoluent)
  // ARTICLES CRÉÉS EN MAGASIN (§11.6) — la caisse dépose, le back-office tranche.
  //
  // ⚠️ `mutable` : un brouillon ÉVOLUE après son premier envoi. Il se vend encore (`sold_count`
  // monte), l'opérateur complète un fournisseur ou un prix d'achat. Sans le drapeau, la projection
  // serveur figerait le brouillon à son premier état — l'écran de validation montrerait « vendu 1
  // fois » sur un article qui en est à quarante.
  //
  // ⚠️ PAS D'AGRÉGAT : un brouillon est une ligne, pas une pièce. Ses éventuelles déclinaisons
  // n'existent qu'après validation, quand il est devenu un article.
  article_draft:   { name: 'article_draft',   direction: 'up',   entity: 'article_draft',     key: 'id', mutable: true },
  // ⚠️ `bi` et non `up` : le device SUPPRIME des rappels (§13.6) — un tombstone est interdit sur un canal
  // `up` append-only. L'échéancier est HORS FISCAL : l'upsert/delete arbitré serveur est légitime.
  payment_reminder:{ name: 'payment_reminder',direction: 'bi',   entity: 'payment_reminder',  key: 'id' }, // échéancier §P.1
} as const satisfies Record<string, ChannelDef>;

export type ChannelName = keyof typeof CHANNELS;

/** La colonne qui porte la disposition d'une ligne de ce canal — `null` s'il n'est pas filtré par disposition. */
export function layoutKeyColumn(name: string): string | null {
  return isChannel(name) ? ((CHANNELS[name] as ChannelDef).layoutKey ?? null) : null;
}

/** Canal déclaré en avance (table pas encore déployée) — son absence en base est ATTENDUE, pas une panne. */
export function isPendingChannel(name: string): boolean {
  return isChannel(name) && (CHANNELS[name] as ChannelDef).pending === true;
}

// ── Delta descendant — `ChangeRecord` (pull) ─────────────────────────────────────────────────
export type SyncOp = 'upsert' | 'delete'; // 'delete' = TOMBSTONE (§13.2) ; le fiscal n'est jamais tombstoné
export interface ChangeRecord {
  channel: string;
  entity: string;
  entityId: string;               // ULID / clé métier
  op: SyncOp;
  rev: string;                    // révision serveur — base du curseur, MONOTONE par canal
  payload: unknown | null;        // ligne complète si upsert ; null si delete
  serverTime: number;             // epoch ms — HEURE DE CONFIANCE (§30.6)
}

// ── Mutation montante — `Mutation` (push), dérivée d'une ligne `outbox` (db-schema/sync.ts) ──────────
// L'`id` (ULID) est la CLÉ D'IDEMPOTENCE : un rejeu du même id ne crée jamais de doublon.
// ⚠️ `channel` N'EST PAS une colonne d'`outbox` (qui n'a que `entity`) : le moteur device le RÉSOUT
// (entité→canal) au moment de bâtir la Mutation. Décision à figer en brique 5 (`sync-client`) :
// dériver depuis l'entité, ou AJOUTER une colonne `channel` à `outbox`. Cf. CADRAGE-Sync-Substrat §6.
export interface Mutation {
  id: string;                     // ULID — clé d'idempotence (= outbox.id)
  channel: string;
  entity: string;
  entityId: string;
  op: SyncOp;
  payload: unknown;
  clientTime: number;             // epoch ms local (INDICATIF ; le serveur garde son heure de confiance)
}

/**
 * Fabrique un identifiant d'idempotence local — celui d'une mutation, et celui d'un objet qui en
 * deviendra une (une zone de comptage porte le sien dès son ouverture).
 *
 * ⚠️ IL VIT DANS LE CONTRAT PARCE QUE LES DEUX BOUTS EN DÉPENDENT : c'est la clé sur laquelle le
 * serveur DÉDOUBLONNE. Écrite dans chaque application, elle divergerait de forme sans que rien ne
 * l'attrape — deux appareils du même magasin produiraient des clés de familles différentes, et le
 * jour où l'une entre en collision avec l'autre, c'est une quantité qui disparaît.
 *
 * ⚠️ CE N'EST PAS UN SECRET, ET IL NE DOIT PAS EN AVOIR L'AIR. `Math.random` suffit à ne pas
 * collisionner ; l'employer pour une clé de session ou de chiffrement serait une faute, et c'est
 * pour ça que le nom dit « mutation » et non « id ».
 */
export function newMutationId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}


// ── Requêtes / réponses ──────────────────────────────────────────────────────────────────────
export const GENESIS = null; // curseur « depuis l'origine » → full-pull / restauration (§16.2.2)

export interface PullRequest {
  cursors: Record<string, string | null>; // curseur par canal (null = GENESIS)
  /**
   * LE CURSEUR UNIQUE — « j'en suis là, sur TOUT ce que mon profil consomme » (2026-09-02).
   *
   * POURQUOI IL EXISTE. Vingt-huit curseurs pour un seul journal, c'était vingt-huit positions à tenir
   * pour un rang qui est GLOBAL — et la possibilité qu'elles s'écartent, donc un cas à traiter qui
   * n'aurait jamais dû exister. Le rang du journal est un ; la position d'un appareil l'est aussi.
   *
   * ⚠️ IL NE REMPLACE PAS `cursors`, IL LE RÉSUME — et seulement quand c'est vrai. Pendant l'AMORÇAGE,
   * un appareil est à des endroits différents selon le canal (l'un parcourt des projections, l'autre
   * prend déjà son delta) : cet état-là ne se dit pas en un nombre, et la carte reste nécessaire.
   * Un appareil n'envoie donc `cursor` QUE lorsque tous ses canaux sont en delta au même rang.
   *
   * ⚠️ LE SERVEUR ACCEPTE LES DEUX FORMES, et rend TOUJOURS `cursors` : une caisse déjà déployée lit la
   * carte et n'a pas à être mise à jour pour continuer de fonctionner. Il rend EN PLUS `cursor` dès que
   * la position est unique — c'est ce champ qui dit à l'appareil qu'il peut passer à la forme courte.
   */
  cursor?: string | null;
  /**
   * LES CANAUX QUE L'APPAREIL SAIT RECEVOIR — ceux dont il possède la table (`receivableChannels`), ou un sous-ensemble
   * pour un re-téléchargement ciblé. OBLIGATOIRE : le serveur ne sert jamais une table qu'un appareil n'a pas, et sans
   * déclaration il ne saurait pas lesquelles (`servedChannels`).
   */
  channels: string[];
  limit?: number;                          // borne de page (le serveur borne aussi)
}
/** La reconstruction d'un appareil (§16.2.2) — la même déclaration que le tirage, pour la même raison. */
export interface RestoreRequest {
  channels: string[];
  limit?: number;
  /**
   * OÙ REPRENDRE — les curseurs rendus par la page précédente ; absents = GENESIS (première page).
   *
   * ⚠️ 🔴 LES PAGES SUIVANTES D'UNE RECONSTRUCTION PASSENT PAR CETTE ROUTE, JAMAIS PAR LE TIRAGE ORDINAIRE.
   *    Seule la reconstruction sert à une caisse sa PROPRE histoire ; le tirage l'écarte (sinon une caisse
   *    en marche recevrait en boucle ce qu'elle vient d'émettre) tout en avançant son curseur au-delà.
   *    Une reconstruction qui continuerait par le tirage ne rendrait que les pièces de sa première page, et
   *    sauterait les autres pour toujours.
   */
  cursors?: Record<string, string | null>;
}

/**
 * LES DERNIERS NUMÉROS QU'UNE CAISSE A ÉMIS, tels que le SERVEUR les connaît — de quoi reprendre sa
 * numérotation quand une tablette en remplace une autre sous le même n° de caisse.
 *
 * ⚠️ PAR PRÉFIXE DE NUMÉRO, JAMAIS PAR COMPTEUR DE LA CAISSE : le serveur relit les numéros qu'il détient
 *    (`T-S1R02-000027`, `CC-S1R02-000004`, `CLI-S1R02-000012`) avec les lecteurs des cœurs qui les
 *    fabriquent ; c'est la caisse, propriétaire de ses compteurs, qui sait quel préfixe avance lequel.
 *    Le serveur n'a ainsi aucun vocabulaire de compteur à tenir à jour.
 * ⚠️ UN PLANCHER, JAMAIS UNE VALEUR À RECOPIER : la caisse garde le plus grand des deux. Une remontée
 *    encore en route vaut un numéro plus haut sur la caisse que sur le serveur, et reculer rééditerait
 *    des numéros déjà émis.
 */
export interface RegisterCounters {
  registerId: string;
  /** Dernier rang par préfixe (`T`, `FT`, `Z`, `SR`, `AV`, `CC`, `AC`, `BF`, `CLI`) — absent = jamais émis. */
  lastByPrefix: Record<string, number>;
  /** Dernière ligne du journal des événements techniques (JET) de la caisse — 0 = aucune. */
  lastJetLine: number;
  /** Les grands totaux de la caisse tels que le serveur les connaît — le plancher d'une tablette qui la reprend. */
  grandTotals: RegisterGrandTotals;
  /** Le tiroir de la caisse tel que le serveur le connaît — le fond et les chèques qu'une tablette qui la reprend trouve. */
  cash: RegisterCashFloor;
}

/** Le détail d'un comptage de tiroir : quantité par coupure, les pièces puis les billets (clé = la coupure en unités de la devise, `'50'`, `'0.2'`). */
export interface RegisterDrawerCounts {
  coins: Record<string, number>;
  bills: Record<string, number>;
}

/** Un chèque reçu par la caisse qui attend sa remise en banque. */
export interface RegisterPendingCheque {
  pieceNumber: string;
  clientName: string | null;
  /** Ce qu'il en reste à remettre (centimes). */
  amountCents: number;
  /** Son montant ; plus grand que `amountCents` quand une remise partielle en a déjà imputé une part. */
  originalCents: number;
  /** `AAAAMMJJhhmmss` — l'heure de sa pièce, ou du changement de règlement qui l'a fait naître. */
  datetimeFiscal: string;
}

/**
 * LE TIROIR D'UNE CAISSE, RELU AU SERVEUR — ce qu'une tablette qui la reprend sous le même n° trouve dans le tiroir.
 *
 * ⚠️ LES MÊMES RÈGLES QUE LA CAISSE (`@orgazone/core-cash`) : le fond est le comptage de la dernière clôture moins ses
 *    dépôts d'espèces, les chèques sont ceux reçus moins les remises. Sans ce plancher, la neuve proposait le fond par
 *    défaut et ne connaissait aucun chèque : ceux du tiroir n'auraient jamais été proposés à la remise.
 * ⚠️ CE QU'IL NE VOIT PAS : une clôture ou une remise que l'ancienne tablette n'a pas remontée — l'opérateur compte.
 */
export interface RegisterCashFloor {
  /** Ce que la dernière session close a laissé au tiroir ; `null` = aucune session close connue du serveur. */
  drawer: {
    /** La session close, pour la note d'une réouverture du même jour. */
    sessionId: string;
    amountCents: number;
    /** Les coupures de ce reste, ou `null` quand la composition n'est pas connue. */
    counts: RegisterDrawerCounts | null;
    /** La clôture a compté le tiroir (sinon : clôture automatique, montant théorique). */
    closingCounted: boolean;
    /** `AAAAMMJJhhmmss` de la clôture, ou `null`. */
    closedAtFiscal: string | null;
    /** `AAAAMMJJ` — la journée fiscale de la session : rouvrir le même jour en crée une seconde. */
    fiscalDay: string | null;
  } | null;
  /** Les chèques qui attendent leur remise, du plus ancien au plus récent. */
  pendingCheques: RegisterPendingCheque[];
  /** Une remise partielle a été imputée aux plus anciens : la liste peut nommer un chèque à la place d'un autre. */
  attributedOldestFirst: boolean;
}

/**
 * LES GRANDS TOTAUX D'UNE CAISSE, RELUS AU SERVEUR — le PLANCHER d'une tablette qui la reprend sous le même n°.
 *
 * ⚠️ LE CUMUL EST LA SOMME DES PIÈCES que le serveur détient pour ce registre, selon la règle des grands totaux
 *    (`outsideGrandTotalsSql`) : le cumul perpétuel d'une caisse est la somme de ce qu'elle a scellé. L'historique qui
 *    redescend ne couvre que six mois et ne peut pas en tenir lieu.
 * ⚠️ LE DERNIER GRAND TOTAL DE CHAQUE TYPE fait repartir chaque période (Z, jour, mois, exercice) de là où l'ancienne
 *    tablette s'était arrêtée : sans lui, le premier Z de la nouvelle porterait toute l'histoire.
 * ⚠️ CE QU'IL NE VOIT PAS : ce que l'ancienne tablette a scellé sans l'avoir remonté — comme pour les numéros.
 */
export interface RegisterGrandTotals {
  /** GTPCA — cumul TTC algébrique (centimes). */
  inclTaxCents: number;
  /** GTPVA — le même cumul en valeur absolue (centimes). */
  absInclTaxCents: number;
  /** Compteur perpétuel de pièces (trésorerie comprise). */
  pieceCount: number;
  /** Ventilation perpétuelle, canonique `pb:HT:TVA;…`. */
  byRate: string;
  /** Le dernier grand total de chaque type (`GTJ`, `GTM`, `GTA`, `GTS`…) — absent = jamais scellé. */
  lastByType: Record<string, { inclTaxCents: number; byRate: string }>;
}
export interface PullResponse {
  changes: ChangeRecord[];                 // triés par (channel, rev) croissant
  cursors: Record<string, string>;         // nouveau curseur par canal APRÈS ce lot
  /**
   * LA POSITION UNIQUE APRÈS CE LOT — présente dès que tous les canaux du profil sont au MÊME rang.
   * Absente = l'appareil est encore à des endroits différents (amorçage en cours) : il doit garder la
   * carte. C'est donc ce champ, et lui seul, qui autorise un appareil à ne plus tenir que ce nombre.
   */
  cursor?: string;
  hasMore: boolean;                        // true → rappeler ; le curseur n'avance QU'après application locale
  /**
   * Reste À VENIR APRÈS le lot rendu — sert l'indicateur de réception du device.
   *
   * ⚠️ UNE ENTRÉE `total`, ET NON UNE PAR CANAL (2026-09-02). Le tirage étant fusionné (une lecture pour
   * tous les canaux d'un profil), un reste PAR CANAL exigerait un comptage par canal — soit le retour
   * exact du défaut qu'on vient de corriger. Le device, lui, n'a jamais utilisé le détail : il SOMME
   * (`Object.values(remaining).reduce(...)`) pour afficher « N restants ». La carte reste donc une
   * carte — un appareil déjà déployé continue de sommer juste — mais elle ne porte plus qu'un total.
   * Les chemins NON fusionnés (amorçage, `fiscal_history`) gardent leur clé de canal : là, le comptage
   * est déjà fait pour d'autres raisons.
   *
   * ⚠️ Pourquoi le SERVEUR l'annonce, et pourquoi c'est un RESTE et non un total figé : le device ne
   * peut pas connaître la taille de ce qu'il n'a pas encore reçu, et un total pris au départ vieillit
   * (des événements arrivent pendant le transfert). Recalculé à chaque page, ce chiffre est vrai à
   * l'instant où il s'affiche — un opérateur qui voit « 12 480 reçus · 17 832 restants » sait
   * exactement où il en est, là où « page 25 » ne dit rien (§23.0). Absent = le serveur ne le fournit
   * pas (ancien BO) : le device affiche alors le compteur seul, jamais un reste inventé.
   */
  remaining?: Record<string, number>;
}

export interface PushRequest { mutations: Mutation[]; }
// Un accusé PAR item (clé de rapprochement = id). `rejected` = refus de validation → NE PAS réappliquer en boucle.
export type MutationAck =
  | { id: string; status: 'applied';   rev: string }
  | { id: string; status: 'duplicate'; rev: string }
  // ⚠️ `detail` DIT POURQUOI, en clair (« ux_fiscal_jet_register_counter : Key (register_id, line_counter)=(02, 50)
  //    already exists. ») : un refus définitif réduit à son code laisse la caisse, et le support, sans rien à lire —
  //    et le journal du serveur, lui, disparaît à chaque redéploiement. Facultatif : un serveur d'avant ne l'envoie pas.
  | { id: string; status: 'rejected';  error: EnvelopeError; detail?: string };
export interface PushResponse { acks: MutationAck[]; }

export interface HealthData { version: string; serverTime: number } // §16.2.8

/**
 * CONTEXTE de version du device-API — le préfixe sous lequel vivent TOUS les endpoints ci-dessous.
 *
 * ⚠️ POURQUOI `/v1` ET PAS `/v2` (corrigé 2026-08-05). Le « 2 » ne numérotait rien de ce service : il
 * comptait par rapport au connecteur legacy `Slid_Connecteur`, un AUTRE service qu'on a décidé de ne
 * jamais brancher (ACL API V1 abandonnée, §16.0.0ter). Or le versionnement est PAR SERVICE (§16) : la
 * première génération du device-API du back-office est la 1. Les trois autres services de la
 * plateforme (store, console, EDI) servent déjà `/v1` — le BO était le seul intrus, pour une raison
 * qui n'existait plus.
 *
 * ⚠️ POURQUOI C'EST UNE CONSTANTE DU CONTRAT, et pas une valeur de configuration. Elle ne l'était pas :
 * le serveur montait son préfixe en littéral, et l'URL remise aux caisses était recopiée à la main dans
 * une variable d'environnement. Les deux ont divergé au PREMIER déploiement (bêta 2026-08-05) — l'env
 * portait la racine du BO sans le contexte, et toute la sync répondait `route_not_found` : aucune
 * remontée de vente, aucune récupération de catalogue. Un préfixe que les deux bouts doivent connaître
 * EST le contrat ; ce qui dépend de la vigilance humaine finit par diverger (§24.3bis).
 *
 * Le serveur monte AVEC, le client compose AVEC. Personne ne le retape.
 */
export const SYNC_API_PREFIX = '/v1';

/**
 * Compose l'URL absolue d'un endpoint de sync à partir de la RACINE du device-API.
 *
 * ⚠️ `baseUrl` est une RACINE, jamais une racine + contexte de version : `https://bo.example.fr`, et
 * non `https://bo.example.fr/v1`. Le contexte appartient au contrat et n'est écrit qu'ici.
 *
 * ⚠️ AUCUNE NORMALISATION — RUSTINE RETIRÉE (décision utilisateur 2026-08-16, « je veux un système
 * propre sans aucune rustine »). Cette fonction rognait auparavant un `/v<n>` final de la base, parce
 * que des caisses avaient été provisionnées avec `…/v2` pendant la bascule. Tolérer une base malformée
 * la rend permanente : la faute cesse d'avoir un coût, donc plus personne ne la corrige, et le jour où
 * un contexte légitime apparaît dans un chemin la normalisation le mange. Le projet est en BÊTA, aucune
 * caisse en production ne dépend de ce rattrapage — on répare la donnée, pas l'appelant.
 *
 * Une base mal formée produit donc désormais une URL visiblement fausse (`…/v2/v1/sync/pull`), qui
 * échoue tout de suite et se corrige là où est l'erreur : dans le provisioning.
 */
export function syncUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, '')}${SYNC_API_PREFIX}${path}`;
}

// ── LES PHOTOS : une adresse dans la donnée, deux portes pour la lire ─────────────────────────────
/**
 * L'ADRESSE D'UNE IMAGE DÉPOSÉE CHEZ NOUS, telle qu'elle est STOCKÉE (`article_photo.url`, tuile du studio) :
 * relative, suivie de la clé de l'objet. Le back-office la sert telle quelle à ses écrans (session humaine).
 * ⚠️ RELATIVE, JAMAIS ABSOLUE : une adresse absolue graverait le nom d'hôte dans la donnée, et un changement de domaine
 *    ferait pointer dans le vide toutes les photos de toutes les bases.
 */
export const MEDIA_PHOTO_PREFIX = '/v1/media/photo/';

/** La clé d'objet d'une adresse interne ; `null` pour une adresse externe (`http(s)://…`) ou malformée. */
export function mediaKeyOf(url: string): string | null {
  if (!url.startsWith(MEDIA_PHOTO_PREFIX)) return null;
  const key = url.slice(MEDIA_PHOTO_PREFIX.length);
  // ⚠️ Une clé qui remonte n'est pas une clé : elle sortirait de l'espace du magasin.
  return key && !key.includes('..') ? key : null;
}

/**
 * LA PORTE DES APPAREILS pour les mêmes images : une caisse n'a pas de session humaine, elle passe par le serveur de
 * sync avec sa clé, et la clé d'objet suit ce chemin (`syncUrl(base, SYNC_MEDIA_PHOTO_PATH + clé)`).
 */
export const SYNC_MEDIA_PHOTO_PATH = '/sync/media/photo/';


// ── JOURNAL TECHNIQUE DU DEVICE (§21 retail) ─────────────────────────────────────────────────
/**
 * Une ligne du journal applicatif d'une caisse, telle qu'elle remonte au back-office.
 *
 * ⚠️ CE N'EST PAS UN CANAL DE SYNC, ET C'EST DÉLIBÉRÉ. L'`outbox` porte les mutations MÉTIER, avec
 * garantie de livraison et ORDRE : une ligne qui échoue y retient tout ce qui suit. Un flux de
 * diagnostic n'a pas le droit de bloquer la remontée d'une vente — et il doit pouvoir se PERDRE, ce
 * que le contrat de l'outbox interdit. D'où un endpoint à part, best-effort, sans curseur.
 * ⚠️ NI DONNÉE FISCALE NI PII : la règle vaut déjà pour l'écriture locale (`app-log.ts`) ; la remontée
 * ne l'assouplit pas. Ce journal sert à comprendre une panne, pas à reconstituer une activité.
 */
export interface DeviceLogEntry {
  /** Identifiant local, sert à l'idempotence : un lot rejoué n'écrit pas deux fois. */
  id: string;
  /** Heure de l'ÉVÉNEMENT sur l'appareil (epoch ms) — pas celle de la remontée. */
  ts: number;
  level: 'debug' | 'info' | 'warn' | 'error' | 'fatal';
  /** Catégorie courte : boot, crash, sync, tpe, print… */
  tag: string;
  message: string;
  stack?: string | null;
  appVersion?: string | null;
  appBuild?: string | null;
}

export interface DeviceLogsRequest { entries: DeviceLogEntry[] }
/**
 * Ce que le serveur a réellement retenu.
 * ⚠️ `accepted` PEUT ÊTRE INFÉRIEUR à ce qui est envoyé (doublons déjà connus) : c'est une information,
 * pas une erreur. Le device marque comme remontées les lignes du lot dès que l'appel a réussi.
 */
export interface DeviceLogsResponse { accepted: number }

// ── Endpoints (RELATIFS au contexte `SYNC_API_PREFIX`, §16.2.8 / §13.2) ──────────────────────
export const SYNC_ENDPOINTS = {
  push:    { method: 'POST', path: '/sync/push' },
  pull:    { method: 'POST', path: '/sync/pull' },        // POST : porte une carte de curseurs multi-canaux
  health:  { method: 'GET',  path: '/sync/health' },
  restore: { method: 'POST', path: '/sync/restore/:deviceId' }, // full-pull GENESIS, reconstruction (§16.2.2) — POST : porte la déclaration
  // ⚠️ BEST-EFFORT ASSUMÉ : ni curseur, ni accusé par ligne, ni rejeu garanti. Un journal qui
  // exigerait des garanties de livraison finirait par bloquer ce qu’il observe.
  logs:    { method: 'POST', path: '/sync/logs' },          // journal technique du device (§21)
  // ⚠️ POSÉE AVANT LE COMPTAGE, PAS APRÈS : le serveur savait déjà refuser une zone sans campagne,
  // mais seulement à la soumission — l'opérateur l'apprenait après avoir compté. Elle ne bloque
  // rien : la zone reste sur l'appareil, et le siège ne parque jamais un comptage sans campagne.
  inventory: { method: 'GET', path: '/scan/inventory' },   // y a-t-il un inventaire ouvert ici ?
  // ⚠️ CONFORT, JAMAIS GARANTIE : elle demande du réseau AU MOMENT de l'ouverture, et un terminal
  // en réserve sans signal comptera très bien sans s'annoncer. C'est pourquoi elle est SÉPARÉE de la
  // soumission, qui, elle, tolère l'absence de réseau par construction. Le terminal ne doit jamais
  // attendre sa réponse pour commencer à scanner.
  zoneOpen: { method: 'POST', path: '/scan/zones/open' },  // « ce terminal commence à compter ce rayon »
  // ⚠️ SANS LA FERMETURE, L'ANNONCE NE SE RÉTRACTE JAMAIS : une zone mise en pause puis jetée
  // laissait au siège un « comptage en cours » éternel, sur une carte verrouillée que personne ne
  // pouvait plus compter à la douchette.
  zoneClose: { method: 'POST', path: '/scan/zones/close' }, // « ce terminal ne compte plus ce rayon »
  // ⚠️ EN LIGNE, À LA DEMANDE (Mobile Partner §8.4) : le terminal résout l'IDENTITÉ d'un article en local
  // mais ne porte ni le stock ni le prix du jour — volatils, faux dès la vente suivante en caisse. Il
  // demande au moment où l'opérateur regarde ; hors ligne il dit « réseau requis », jamais un chiffre d'hier.
  // Clé : `?gencode=` (un scan) ou `?articleCode=` (une fiche choisie dans la recherche).
  stockLookup: { method: 'GET', path: '/scan/stock' },
  // ⚠️ LA MÊME ROUTE POUR LA CAISSE ET LE TERMINAL : « combien ce client a-t-il, quels titres tient-il ? »
  // est une seule question, et une seconde route aurait fini par sommer le grand livre autrement.
  clientValue: { method: 'GET', path: '/sync/client-value/:clientId' },
  // ⚠️ DEMANDÉE À LA MISE EN SERVICE, AVANT LA PREMIÈRE VENTE : une tablette qui remplace une caisse perdue
  // reprend ses numéros là où le serveur les a vus s'arrêter — sinon elle rééditerait des tickets qui
  // existent déjà au siège, et chacun serait refusé. En ligne par construction.
  registerCounters: { method: 'GET', path: '/sync/register-counters' },
  // ⚠️ TIRÉ AVANT DE SCANNER une réception « contre bon » (Mobile Partner §8.2) : l'annoncé, ligne par
  // ligne. En ligne par construction — la réception AVEUGLE, elle, n'a besoin de rien.
  receiptExpected: { method: 'GET', path: '/scan/receipts/expected' },   // ?ref=<numéro de bon>
  // ⚠️ EN LIGNE, À LA DEMANDE (Mobile Partner §8.3) : les transferts EN TRANSIT vers ce magasin, avec leur
  // annoncé — ce que « À traiter » liste. Un souvenir local ferait ouvrir un contrôle sur un colis qu'un
  // autre terminal a déjà reçu.
  transfersIncoming: { method: 'GET', path: '/scan/transfers/incoming' },
} as const;

// ── Clés `message` du socle de sync (registre central partagé serveur ↔ tablette, §16.2.7) ───
// ⚠️ Convention NORMATIVE : anglais, snake_case, `<sujet>_<résultat>`, STABLE dans le temps (une clé
// renommée casse l'i18n de la tablette). Ce sont des CLÉS de message, PAS le champ `code` (= HTTP réel).
export const SYNC_MESSAGES = {
  pulled:         'sync_pulled',
  pushed:         'sync_pushed',
  pushPartial:    'sync_push_partial',    // au moins un item `rejected`
  healthOk:       'health_ok',
  logsReceived:   'logs_received',
  unauthorized:   'unauthorized',
  cursorUnknown:  'cursor_unknown',       // curseur périmé/purgé → forcer un full-pull
  payloadInvalid: 'payload_invalid',
  channelReadonly:'channel_readonly',     // push sur un canal `down` (ou pull d'un canal `up`)
  rateLimited:    'rate_limited',
  batchTooLarge:  'batch_too_large',
  internalError:  'internal_error',
  rebuildTargetNotEmpty: 'rebuild_target_not_empty', // mode `full` refusé : la cible contient déjà des données
  /** La mutation se réclame d'un AUTRE registre que celui authentifié — refus définitif (§audit 2026-08-06 §1).
   *  Une caisse ne peut écrire que dans SA chaîne : accepter reviendrait à la laisser corrompre celle d'une
   *  caisse voisine du même magasin (mêmes numéros de séquence, même chaînage). */
  registerMismatch: 'register_mismatch',
  /** Faute de données PERMANENTE côté serveur (colonne/table inconnue, contrainte violée) — refus DÉFINITIF.
   *  ⚠️ Distinct de `internal_error`, qui reste transitoire et se rejoue : sans cette distinction, une
   *  mutation impossible à écrire revient en tête de file à chaque cycle et bloque tout ce qui suit
   *  (constat 🔴 de l'audit 2026-08-06 §2). */
  writeRejected:  'write_rejected',
  /** Deux MACHINES distinctes se déclarent sur le MÊME n° de caisse (identités d'appareil différentes).
   *  ⚠️ Refus, et non simple alerte : la chaîne de signatures NF525 est PAR REGISTRE — deux machines sous
   *  le même numéro produiraient deux suites divergentes sous une seule identité fiscale, irréparable
   *  après coup. C'est le n° de caisse SAISI à la main qui rend le cas possible (§13.1bis). */
  registerConflict: 'register_conflict',
  /** Tirage ou reconstruction SANS la liste des canaux que l'appareil sait recevoir — refus : le serveur ne devine pas. */
  channelsRequired: 'channels_required',
} as const;

// (Les codes HTTP viennent de `@orgazone/api-envelope` — ré-exportés plus haut, jamais redéfinis ici.)

// ── Helpers PURS (le seul « code » du contrat : des garde-fous, pas de la logique métier) ──────

/** Vrai si `name` est un canal déclaré. */
export function isChannel(name: string): name is ChannelName {
  return Object.prototype.hasOwnProperty.call(CHANNELS, name);
}

/** Sens d'un canal (undefined si canal inconnu). */
export function channelDirection(name: string): SyncDirection | undefined {
  return isChannel(name) ? CHANNELS[name].direction : undefined;
}

/** Un canal accepte-t-il un PUSH (montant) ? Seuls `up` et `bi` l'acceptent ; `down` est en lecture seule. */
export function isPushable(name: string): boolean {
  const d = channelDirection(name);
  return d === 'up' || d === 'bi';
}

/** Un canal descend-il vers le device (PULL) ? `down` et `bi`. */
export function isPullable(name: string): boolean {
  const d = channelDirection(name);
  return d === 'down' || d === 'bi';
}

// ── PROFILS D'APPAREIL — ce qu'un TYPE d'appareil consomme (2026-09-02) ──────────────────────────────
//
// POURQUOI CE REGISTRE EXISTE. Le tirage demandait au device QUELS canaux il voulait (`PullRequest.channels`).
// Deux appareils du même magasin pouvaient donc porter des curseurs très écartés — celui qui demandait peu
// laissait le reste immobile — et la seule parade était un seuil de divergence à régler à la main.
// Le périmètre devient une propriété de l'APPAREIL, décidée au serveur : la divergence n'existe plus, parce
// qu'un appareil ne peut plus demander moins que son profil.
//
// ⚠️ ET CE N'EST PAS QU'UNE LISTE DE CANAUX. Une caisse et un terminal de scan connaissent tous deux les
// articles — mais pas les MÊMES articles : la caisse a besoin du secteur, du prix d'achat, de la TVA ;
// le terminal n'a ni écran ni colonne pour les ranger, il lui faut désignation, gencode, coloris, taille.
// Le profil dit donc AUSSI la forme servie. C'est la généralisation d'une garde qui existe déjà
// (`colonnes.ts` sert des colonnes NOMMÉES, jamais `SELECT *`) : le profil devient la source de cette liste.
//
// ⚠️ LE JOURNAL RESTE UN. On n'écrit jamais deux événements pour un même fait — un « gras » et un
// « maigre » seraient deux vérités pour la même chose, et le double du stockage. Le rétrécissement se fait
// AU SERVICE, sur le même événement.
//
// ⚠️ ÉLARGIR UN PROFIL (un canal de plus, une colonne de plus) EXIGE UN RÉ-AMORÇAGE de ce qui est ajouté :
// l'appareil ne l'a jamais reçu, il doit le recevoir depuis l'origine. C'est ce qui remplace le seuil de
// divergence — une règle, pas un réglage.
export type DeviceProfileName = 'caisse' | 'mobile-partner';

export interface DeviceProfile {
  readonly name: DeviceProfileName;
  /** Les canaux TIRABLES que ce type d'appareil consomme. Un canal absent ne lui est jamais servi. */
  readonly channels: readonly string[];
}

/** Le profil par DÉFAUT — tout appareil qui ne s'annonce pas est une caisse (la flotte d'aujourd'hui). */
export const DEFAULT_DEVICE_PROFILE: DeviceProfileName = 'caisse';

/** En-tête par lequel un appareil annonce son type. Absent = `caisse` (rétrocompatible). */
export const DEVICE_PROFILE_HEADER = 'x-device-profile';

/**
 * En-tete par lequel un TERMINAL DE SCAN s'annonce sur les routes de zones (`/scan/zones*`).
 *
 * ⚠️ CE N'EST PAS `x-device-id`, ET LES DEUX COEXISTENT SUR LE MEME SERVEUR. Les routes de sync
 * lisent l'un, les routes de zones l'autre, et rien ne les reliait : une chaine ecrite a la main de
 * chaque cote, dont la moindre faute de frappe rend un 401 muet. Le nommer ici est ce qui empeche
 * les deux bouts de diverger.
 *
 * ⚠️ LA VALEUR NE CHANGE PAS. C'est un en-tete DEJA servi par un serveur en production : le
 * renommer engagerait la flotte et le serveur dans la meme livraison (Forme J). On lui donne un
 * nom en TypeScript, on ne touche pas au fil.
 */
export const SCANNER_ID_HEADER = 'x-scanner-id';

/**
 * L'IDENTITÉ D'UNE CAISSE, telle qu'elle se SCELLE — deux chiffres au moins (« 01 »), jamais tronquée.
 *
 * ⚠️ 🔴 ELLE ÉTAIT ÉCRITE D'UN SEUL CÔTÉ. La tablette normalisait à deux chiffres (convention du
 * numéro de pièce `T-S1R01-000001`, §30.7.1) ; la Console déclarait le nombre nu (« 1 »). Mesuré sur
 * la bêta : la MÊME machine déclarée caisse « 1 » au back-office et scellant comme « 01 » — les écrans
 * qui unissent les deux sources montraient deux caisses pour une, et la santé de flotte ne retrouvait
 * jamais la caisse qu'elle cherchait (Forme A appliquée à une identité).
 *
 * ⚠️ ELLE VIT ICI, PAS DANS UNE APP : la caisse la scelle, la Console la déclare, le serveur de sync
 * la reçoit — trois consommateurs d'un même fait. Une deuxième définition redivergerait.
 *
 * ⚠️ AU-DELÀ DE DEUX CHIFFRES, ON NE TRONQUE PAS : la caisse 123 reste « 123 » (`R123`). Tronquer
 * confondrait deux caisses sous un même code, donc deux chaînes fiscales sous un même registre.
 * ⚠️ UN CODE NON NUMÉRIQUE EST RENDU TEL QUEL : la reprise legacy nomme ses registres « LEGACY-C1 »,
 * et ce n'est pas à cette fonction de décider qu'ils sont faux.
 */
export function registerIdOf(code: number | string): string {
  const texte = String(code).trim();
  const n = /^[0-9]+$/.test(texte) ? parseInt(texte, 10) : Number.NaN;
  return Number.isFinite(n) && n >= 1 ? String(n).padStart(2, '0') : texte;
}

/** Préfixe des registres de RÉCUPÉRATION — voir `recoveryRegisterIdOf`. */
export const RECOVERY_REGISTER_PREFIX = 'RECOVERY-R';

/**
 * LE REGISTRE DE RÉCUPÉRATION D'UNE CAISSE RECONSTRUITE — `RECOVERY-R01`.
 *
 * POURQUOI IL EXISTE. Quand une tablette meurt, on veut redescendre SON histoire sur la tablette qui
 * la remplace : l'opérateur doit retrouver ses ventes, ses tickets, l'historique de ses clients. Mais
 * cette histoire ne peut pas revenir sous le registre ACTIF — la caisse devrait alors relier sa
 * prochaine signature à la précédente, qu'elle n'a plus. Séquences, chaînage et grands totaux
 * deviendraient faux, et elle ne pourrait plus prouver l'intégrité de ce qu'elle émet.
 *
 * ⚠️ C'EST EXACTEMENT LE PATRON DE `LEGACY-C<n>`, et ce n'est pas un hasard : les deux servent une
 * histoire qu'on n'a pas signée et qu'on ne prétend pas avoir signée. Rien n'a besoin de les nommer
 * pour les exclure des chaînes — les lecteurs filtrent POSITIVEMENT sur le registre de session, donc
 * tout autre registre est hors chaîne par construction.
 *
 * ⚠️ ELLE VIT DANS LE CONTRAT parce que DEUX moitiés en dépendent : le serveur RÉÉCRIT le registre en
 * servant la récupération, la caisse le RECONNAÎT pour distinguer à l'écran ce qu'elle a émis de ce
 * qu'elle a reçu. Une seconde définition divergerait, et la divergence serait muette (Forme A).
 *
 * ⚠️ « RECOVERY » ET NON « ARCHIVE » (décision utilisateur). Le mot est LU — par un opérateur devant
 * son historique, par un auditeur devant une base — et « archive » dit « du vieux », ce qui inquiète
 * sans rien expliquer. Il nomme surtout la NATURE, là où `LEGACY-C<n>`, son voisin, nomme l'ORIGINE :
 * d'où viennent ces pièces, et pourquoi elles ne sont pas signées ici. En toutes lettres plutôt
 * qu'abrégé (`RECOV`) — `register_id` est un `text` sans contrainte, et une abréviation se
 * réexplique à chaque lecteur.
 *
 * ⚠️ IL PASSE PAR `registerIdOf` : la récupération de la caisse « 1 » et celle de la caisse « 01 »
 * doivent être LA MÊME — c'est précisément la double identité déjà relevée à l'audit des bases magasin.
 */
export function recoveryRegisterIdOf(code: number | string): string {
  return `${RECOVERY_REGISTER_PREFIX}${registerIdOf(code)}`;
}

/**
 * LES SENTINELLES ABRÉGÉES POUR UN ÉCRAN ÉTROIT — table FERMÉE, jamais une troncature devinée.
 *
 * ⚠️ ELLE N'ABRÈGE QUE LE MOT, JAMAIS LE NUMÉRO : `LEGACY-C1` → `LEG-C1` garde son `C1`, qui est
 * l'information. Un raccourci mécanique (« les trois premières lettres ») ferait collider une
 * sentinelle future avec une existante sans que rien ne le dise.
 * ⚠️ CE N'EST PAS UNE SECONDE DÉFINITION DE LA VALEUR — celle-ci est produite ailleurs (`LEGACY-C<n>`
 * par la reprise Console, `RECOVERY-R<n>` par `recoveryRegisterIdOf`) et reste INTACTE en base. Ici on
 * ne fait que la rendre plus courte à lire.
 * ⚠️ UNE SENTINELLE INCONNUE DE CETTE TABLE RESTE ENTIÈRE : mieux vaut large que faux.
 */
const REGISTER_ABBREVIATIONS: readonly (readonly [string, string])[] = [
  ['LEGACY-', 'LEG-'],
  ['RECOVERY-', 'REC-'],
];

/**
 * LE REGISTRE TEL QU'IL SE DONNE À LIRE — « 01 » devient « R01 », une sentinelle est ABRÉGÉE, jamais
 * travestie.
 *
 * 🔴 POURQUOI ELLE EXISTE : l'écran inventait un numéro. La caisse rendait `Number(register_id) || 1`,
 * or `Number('LEGACY-C1')` vaut `NaN` — donc **toute pièce d'un registre non numérique s'affichait
 * « caisse 1 »**. Sur la bêta, les 10 673 pièces reprises d'un ancien logiciel étaient attribuées à
 * une caisse qui ne les avait jamais produites, sans que rien ne le signale (§23.0, donnée inventée).
 * Le repli `|| 1` est le coupable : il transforme « je ne sais pas lire ceci » en « c'est la 1 ».
 *
 * ⚠️ UNE COLONNE, UN SEUL VOCABULAIRE (décision utilisateur) : `R01`, `R02`, `LEG-C1`, `REC-R01` se
 * lisent tous de la même façon — un code de registre. La caisse rendait auparavant « №1 », donc deux
 * façons de lire dans la même colonne dès qu'un historique reçu s'y trouvait.
 *
 * ⚠️ RENDU SEULEMENT, JAMAIS STOCKÉ : le `register_id` scellé compose le numéro de pièce et devient un
 * paramètre fiscal FIGÉ dès la première signature. ⚠️ Et il ne se raccourcit PAS en base : le
 * back-office interroge l'historique repris par `LIKE 'LEGACY-%'` et `startsWith('LEGACY-')`.
 *
 * ⚠️ MÊME RÈGLE QUE `codeCaisse` (`@orgazone/ui-web`) SUR LES DEUX POINTS QUI COMPTENT — un entier
 * devient `R<nn>`, une sentinelle n'est jamais travestie en numéro. Elle s'en écarte seulement en
 * ABRÉGEANT, parce qu'une colonne de tablette fait 56 px là où le back-office a la place. ⚠️ `ui-web`
 * ne dépend pas de ce paquet : la consolidation ajoute une dépendance et se DÉCIDE, elle ne se suppose
 * pas — d'ici là, ce sont les deux points ci-dessus qui ne doivent jamais diverger.
 */
export function registerLabel(registerId: string): string {
  const texte = String(registerId).trim();
  if (/^\d+$/.test(texte)) return `R${texte.padStart(2, '0')}`;
  const abrege = REGISTER_ABBREVIATIONS.find(([mot]) => texte.startsWith(mot));
  return abrege ? `${abrege[1]}${texte.slice(abrege[0].length)}` : texte;
}

/** Ce registre est-il un vrai numéro de caisse (par opposition à une sentinelle d'historique reçu) ? */
export function isNumericRegister(registerId: string): boolean {
  return /^\d+$/.test(String(registerId).trim());
}

/**
 * Ce registre porte-t-il un historique RÉCUPÉRÉ ?
 *
 * ⚠️ UN REGISTRE DE RÉCUPÉRATION N'EST JAMAIS UNE CHAÎNE ACTIVE : rien de ce qu'il porte n'a été signé
 * par la caisse qui le détient, et rien ne doit jamais y être ajouté.
 */
export function isRecoveryRegister(registerId: string): boolean {
  return registerId.startsWith(RECOVERY_REGISTER_PREFIX);
}

/**
 * Les profils connus.
 *
 * ⚠️ `caisse` PREND TOUS LES CANAUX TIRABLES, et c'est calculé, pas recopié : une liste tenue à la main
 * oublierait le canal suivant, et la caisse cesserait de le recevoir sans que rien ne le dise.
 * ⚠️ `mobile-partner` est DÉLIBÉRÉMENT VIDE tant que son périmètre n'est pas arrêté (spec
 * `mobile-partner.md` §7 : catalogue et clients en local, stock et fidélité EN LIGNE à la demande).
 * Un profil deviné serait pire qu'absent — il ferait descendre des données que l'appareil ne sait pas
 * ranger. ⚠️ Décision du 2026-09-08 : ce sera un **canal DISTINCT** (table articles restreinte, sans
 * prix d'achat ni famille/secteur/saison), pas une projection du canal `catalog` selon le lecteur —
 * un canal dont le CONTENU dépend de qui le tire donnerait à deux appareils des lignes différentes
 * sous le même curseur.
 *
 * ⚠️ CETTE VALEUR EST FIGÉE : elle circule vers la flotte par l'en-tête `x-device-profile`, et il
 * n'existe aucune fenêtre où le serveur et des appareils déjà livrés basculent ensemble. La changer
 * est une MIGRATION coordonnée, jamais un renommage (Forme J du catalogue).
 */
export const DEVICE_PROFILES: Record<DeviceProfileName, DeviceProfile> = {
  caisse:   { name: 'caisse',   channels: Object.keys(CHANNELS).filter(isPullable) },
  /**
   * ⚠️ SA LISTE ÉTAIT VIDE, ET UNE LISTE VIDE NE VEUT PAS DIRE « RIEN » (corrigé le 2026-09-09). Côté
   * porte device, `profile.channels.length > 0 ? profile.channels : PULLABLE` : une liste vide vaut
   * « aucune restriction déclarée » et retombe sur TOUS les canaux tirables. Un Mobile Partner qui
   * se serait mis à synchroniser aurait donc reçu le catalogue ENTIER — prix d'achat, familles,
   * saisons, photos —, exactement ce que le cadrage voulait éviter en demandant une table articles
   * restreinte. Le vide était lu comme une permission.
   *
   * ⚠️ CE QUI EST DÉCLARÉ AUJOURD'HUI EST CE QUI EST BRANCHÉ, ET RIEN DE PLUS. Les opérateurs (pour
   * l'identification, §5), les réglages descendus (§6) et les consignes de BLOCAGE. Le CATALOGUE
   * n'y est PAS : il attend son canal restreint, qui est un travail à part — l'ajouter ici avant lui
   * rouvrirait le trou qu'on vient de fermer.
   *
   * ⚠️ `blockade` EST LE CHEMIN QUI SURVIT À UNE CONSOLE INJOIGNABLE. Le tirage de configuration
   * apprend un blocage immédiatement, mais il exige que la Console réponde ; ce canal passe par la
   * porte de synchronisation. Sans lui, une Console en panne rendrait le kill-switch inopérant — et
   * c'est précisément le moment où on veut pouvoir couper un appareil.
   *
   * ⚠️ AJOUTER UN CANAL À UN PROFIL EST SANS DANGER POUR LES APPAREILS DÉJÀ EN SERVICE, pour deux
   * raisons qui ne se remplacent pas. Le CURSEUR : la porte le déplie sur ce que CET appareil a déjà
   * reçu, et un canal dont elle n'a aucune trace repart de l'ORIGINE (`unfoldCursor`). La TABLE : un
   * canal n'est servi qu'à l'appareil qui le DÉCLARE (`servedChannels`) — sans quoi un appareil qui n'a
   * pas la table recevrait des lignes qu'il ne peut pas écrire, et sa synchronisation s'arrêterait.
   */
  'mobile-partner': { name: 'mobile-partner', channels: ['operators', 'settings', 'blockade', 'catalog', 'clients'] },
};

/** Le profil d'un appareil, `caisse` par défaut — un en-tête inconnu ne fabrique jamais un profil vide. */
export function deviceProfile(name: string | undefined | null): DeviceProfile {
  const n = String(name ?? '').trim().toLowerCase();
  return (n in DEVICE_PROFILES ? DEVICE_PROFILES[n as DeviceProfileName] : DEVICE_PROFILES[DEFAULT_DEVICE_PROFILE]);
}

// ---------------------------------------------------------------------------
// LA PORTE DES CANAUX DÉCLARÉS — un appareil ne reçoit que ce qu'il sait écrire
// ---------------------------------------------------------------------------

/**
 * CE QU'UN APPAREIL SAIT RECEVOIR — les canaux descendants dont il possède la TABLE, et celles de leurs enfants.
 *
 * ⚠️ DÉRIVÉ DE SON SCHÉMA, jamais d'une liste : une table ajoutée à l'APK rend son canal recevable sans autre
 * geste, et un canal déclaré d'avance dont la table manque n'est pas demandé — il n'y aurait rien pour l'écrire.
 */
export function receivableChannels(tables: Iterable<string>): string[] {
  const have = new Set(tables);
  return (Object.values(CHANNELS) as readonly ChannelDef[])
    .filter((ch) => isPullable(ch.name) && have.has(ch.entity) && (ch.aggregate ?? []).every((child) => have.has(child.entity)))
    .map((ch) => ch.name);
}

/**
 * LES CANAUX DESCENDANTS QUI REMPLISSENT CES TABLES — ceux dont c'est l'entité, ou celle d'un enfant de leur agrégat.
 *
 * ⚠️ 🔴 À QUOI ÇA SERT : quand un appareil ajoute une colonne à une table qu'il a déjà, ses lignes en service n'ont pas
 *    cette colonne — elles sont arrivées par une version qui l'ignorait, et le serveur ne les renverra pas tant qu'elles
 *    ne changent pas. Leurs canaux doivent alors être relus depuis le début. Une famille publiée « Ses articles : texte
 *    seul » avant l'APK qui connaît ce réglage gardait ainsi, sur la tablette, des articles en vignette.
 */
export function channelsFilling(tables: Iterable<string>): string[] {
  const touched = new Set(tables);
  return (Object.values(CHANNELS) as readonly ChannelDef[])
    .filter((ch) => isPullable(ch.name) && (touched.has(ch.entity) || (ch.aggregate ?? []).some((child) => touched.has(child.entity))))
    .map((ch) => ch.name);
}

/**
 * LES CANAUX À RELIRE DEPUIS LE DÉBUT après qu'un appareil a ajouté ces colonnes (`table.colonne`) à sa base.
 * ⚠️ LA DÉCISION VIT ICI, UNE FOIS : la caisse et Mobile Partner remettent ces curseurs au début, chacun dans sa base.
 */
export function channelsToReread(addedColumns: readonly string[]): string[] {
  return channelsFilling(addedColumns.map((c) => c.slice(0, c.indexOf('.'))));
}

/**
 * LE PÉRIMÈTRE SERVI À UNE REQUÊTE — le profil de l'appareil, borné par ce qu'il déclare savoir recevoir.
 *
 * ⚠️ 🔴 POURQUOI LA DÉCLARATION EXISTE. Un appareil ignore une COLONNE inconnue, pas une TABLE : une ligne d'un canal
 *    dont il n'a pas la table fait échouer sa page, le curseur n'avance plus, et la même page revient à chaque cycle —
 *    l'appareil ne se synchronise plus DU TOUT. Le serveur ne sert donc que ce que l'appareil dit savoir écrire.
 * ⚠️ UNE DÉCLARATION NE PEUT QUE RESTREINDRE, jamais élargir : un appareil ne se sert pas de ce que son profil
 *    ne lui destine pas.
 * ⚠️ ELLE EST OBLIGATOIRE : une requête qui n'en porte pas est refusée en amont (`channels_required`) — aucun
 *    ensemble de repli n'est servi à sa place.
 */
export function servedChannels(profileChannels: readonly string[], declared: readonly string[]): string[] {
  return profileChannels.filter((ch) => declared.includes(ch));
}

/** Canal STRICTEMENT append-only (`up` non mutable) : la projection serveur n'écrase jamais une ligne
 *  existante. Les canaux `up` MUTABLES (session de caisse, retouche) sont projetés en upsert. */
export function isAppendOnlyChannel(name: string): boolean {
  if (!isChannel(name)) return false;
  const def = CHANNELS[name] as ChannelDef;
  // Un canal `down` peut aussi être append-only (drapeau explicite) : contenu SIGNÉ redescendu, que le
  // device n'a le droit que d'INSÉRER — jamais de réécrire ni d'effacer.
  return def.appendOnly === true || (def.direction === 'up' && !def.mutable);
}

/**
 * Canal dont l'APPAREIL possède l'issue : la descente n'y insère que du NOUVEAU.
 *
 * ⚠️ Le résultat ressemble à celui d'`isAppendOnlyChannel` (insert-si-absent) et la RAISON n'est pas
 * la même : là-bas le contenu est immuable, ici il change sans cesse — c'est le DROIT D'ÉCRIRE qui est
 * partagé. Les confondre ferait croire qu'on peut retirer l'un des deux.
 */
export function isSenderOwnedChannel(name: string): boolean {
  return isChannel(name) && (CHANNELS[name] as ChannelDef).senderOwned === true;
}

/** Enfants d'un canal AGRÉGAT (tableau vide si le canal porte une table simple). */
export function aggregateChildren(name: string): readonly AggregateChild[] {
  return (isChannel(name) ? (CHANNELS[name] as ChannelDef).aggregate : undefined) ?? [];
}

/**
 * LES COLONNES QUE LE SERVEUR DÉRIVE — aucune synchronisation ne les transporte, dans aucun sens.
 *
 * ⚠️ POURQUOI CETTE LISTE EXISTE (audit fidélité 2026-08-31). `client.loyalty_balance_cents` était
 * déclaré DÉRIVÉ des deux côtés — le DAO de la caisse écrit « le pousser créerait une 2ᵉ vérité », le
 * producteur du back-office l'exclut de ses événements « l'écraser depuis un import ferait perdre des
 * points sans trace ». Mais l'instantané client de la caisse le poussait quand même : il relit la
 * ligne ENTIÈRE (`SELECT *`), et le canal `clients` est `bi`. Corriger un simple NUMÉRO DE TÉLÉPHONE
 * sur une caisse renvoyait donc SON solde au serveur, qui le redescendait sur les autres — et des
 * gains réellement acquis disparaissaient, sans trace, par le geste le plus banal du comptoir.
 *
 * ⚠️ LA GARDE VIT DANS LE CONTRAT, ET S'APPLIQUE D'ABORD CÔTÉ SERVEUR. Ne la poser que sur la caisse
 * ne réparerait rien : **les APK déjà installées continueront de pousser la colonne** jusqu'à leur
 * mise à jour, et il n'y a aucune date à laquelle on puisse affirmer que la dernière est remplacée.
 * Une règle que les deux bouts doivent connaître EST le contrat (§24.3bis).
 *
 * ⚠️ CE N'EST PAS UN REFUS, C'EST UN RETRAIT. Rejeter la mutation ferait perdre la correction de
 * téléphone qui la portait ; on retire la colonne et on écrit le reste.
 */
/**
 * ⚠️ DEUX RÉGIMES, parce que « dérivé » ne dit pas encore QUI fait autorité :
 *  · `device` — **chaque bout tient la sienne** et ne l'échange jamais. Le solde de cagnotte : la
 *    caisse le projette depuis SON grand livre, le serveur depuis le sien. Le faire descendre ferait
 *    clignoter l'écran de vente entre deux valeurs justes-mais-décalées, le temps qu'un mouvement tout
 *    juste scellé fasse l'aller-retour.
 *  · `server` — **le serveur fait autorité et la valeur DESCEND**. Le palier : il se calcule sur le
 *    cumul d'achats d'une fenêtre glissante, donc sur tout le grand livre, ce qu'une caisse ne voit
 *    pas. Elle doit pourtant le connaître HORS LIGNE (c'est lui qui choisit le taux d'acquisition au
 *    moment de la vente) : il descend par le canal `clients`, et ne remonte jamais.
 * Le point commun, et la règle : **rien de dérivé ne remonte**.
 */
export const DERIVED_COLUMNS: Readonly<Record<string, Readonly<Record<string, 'device' | 'server'>>>> = {
  client: {
    loyalty_balance_cents: 'device',
    tier: 'server',
  },
};

/** Retire d'une ligne les colonnes retenues (copie ; la ligne d'origine n'est pas touchée). */
function sansColonnes(row: Record<string, unknown>, retirer: readonly string[]): Record<string, unknown> {
  if (!retirer.length) return row;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) if (!retirer.includes(k)) out[k] = v;
  return out;
}

/**
 * Applique un retrait à une charge de synchronisation — ligne simple ou AGRÉGAT.
 * ⚠️ Les deux formes, parce que le canal `clients` est justement un agrégat : ne traiter que la ligne
 * plate laisserait passer exactement le cas qui a motivé la règle.
 */
function retirerDeLaCharge(payload: unknown, retirer: readonly string[]): unknown {
  if (payload === null || typeof payload !== 'object' || !retirer.length) return payload;
  const p = payload as Record<string, unknown>;
  if (p.root && typeof p.root === 'object' && p.children && typeof p.children === 'object') {
    return { ...p, root: sansColonnes(p.root as Record<string, unknown>, retirer) };
  }
  return sansColonnes(p, retirer);
}

const colonnesDerivees = (entity: string, regime?: 'device' | 'server'): string[] =>
  Object.entries(DERIVED_COLUMNS[entity] ?? {})
    .filter(([, r]) => regime === undefined || r === regime)
    .map(([col]) => col);

/**
 * MONTANT (caisse → serveur) : retire TOUTES les colonnes dérivées. Aucune ne se pousse, quel que soit
 * son régime — le serveur les recalcule ou en fait autorité.
 */
export function stripUpstreamDerived(entity: string, payload: unknown): unknown {
  return retirerDeLaCharge(payload, colonnesDerivees(entity));
}

/**
 * DESCENDANT (serveur → caisse) : ne retire que les colonnes dont **chaque bout tient la sienne**.
 * Celles dont le serveur fait autorité traversent — c'est tout l'intérêt de les faire descendre.
 */
export function stripDownstreamDerived(entity: string, payload: unknown): unknown {
  return retirerDeLaCharge(payload, colonnesDerivees(entity, 'device'));
}

/** Entité → canal (le registre est la source unique du sens de synchro ; `outbox` ne stocke pas le canal).
 *  ⚠️ Deux canaux peuvent porter la MÊME entité (`fiscal_piece` est aussi RE-SERVI par `fiscal_history`,
 *  §16.2.8septies). Cette résolution route l'OUTBOX — le sens montant : le canal POUSSABLE gagne donc
 *  toujours. Sans cette préférence, l'ordre d'énumération du registre décidait du routage : une vente
 *  routée vers le canal descendant serait refusée `channelReadonly` et ne partirait JAMAIS. */
export function channelOfEntity(entity: string): string | null {
  let fallback: string | null = null;
  for (const [name, def] of Object.entries(CHANNELS as Record<string, ChannelDef>)) {
    if (def.entity !== entity) continue;
    if (isPushable(name)) return name;
    if (fallback === null) fallback = name;
  }
  return fallback;
}

/** Identifiant de mutation DÉTERMINISTE pour la RECONSTRUCTION [B2] : `rec:<entity>:<rowId>`.
 *  ⚠️ Pourquoi pas un ULID neuf : l'`id` est la clé d'idempotence de bout en bout (`event.id` UNIQUE côté
 *  serveur). Une reconstruction se relance (coupure, reprise, doute) — avec un id neuf, chaque passe créerait
 *  de nouveaux événements ; avec un id dérivé de la ligne, **rejouer est un no-op**. */
export { masterDataId, shortHash, normalizeCode, normalizeCodeSql, foldAccentsSql, slugSql, documentId, normalizeDeliveryNumber } from './master-data-ids';
export {
  SUPPLIER_ACCOUNT_RE, SUPPLIER_TERMS_LIMITS, isEmailAddress, supplierAccountHint, supplierTermsFieldRefusal, supplierTermsRefusals,
} from './supplier-terms';
export type { SupplierTermsField, SupplierTermsFields, SupplierTermsRefusal } from './supplier-terms';
export { REMINDER_METHOD_TEXT, reminderMethodText, type ReminderMethod } from './reminder-methods';
export { ABANDON_REASON_TEXT, abandonReasonText, type AbandonReason } from './abandon-reasons';
// Les PORTÉES des motifs (écart de caisse, remise, crédit de cagnotte) et le motif proposé d'office — la Console les
// sème, le back-office les édite, la caisse les lit.
export {
  JUSTIFICATION_SCOPES, DEFAULT_REASON_CODE, DEFAULTABLE_SCOPES, defaultReasonScope, effectiveDefaultReason, isDefaultableScope,
  isJustificationScope, justificationScopeOf, proposeReasonCode, type DefaultReasonCandidate, type DefaultableScope, type JustificationScope,
} from './justification-scopes';
// La sorte de liste des catégories de frais — la Console la sème, le back-office la tient, la caisse la proposera.
export { EXPENSE_CATEGORY_KIND } from './expense-categories';
// « Aucun groupe » : les appareils d'un magasin se mettent à jour par leur propre store — la Console le choisit, eux l'appliquent.
export { NO_PRIVATE_STORE_UPDATES, followsNoPrivateStore } from './update-group';
export {
  DOCUMENT_NUMBER_FALLBACK, DOCUMENT_NUMBER_MAX, documentNumberRefusal, proposeDocumentNumber, typedDocumentNumber,
  type CivilDay, type DocumentNumberRefusal,
} from './document-number';
export {
  SETTINGS, isKnownSetting, settingScope, isSyncedSetting, isTabletWritableSetting, settingsOfScope,
  FID_SETTING_KEYS, FID_TIER_DEFAULTS, SWITCH_DEFAULTS, isSwitchSetting, switchOn, tierDisplay, tierScaleOf,
  FID_WELCOME_BONUS_DEFAULT, loyaltyLedgerMode, welcomeBonusOf, currencyFractionOf,
  STORE_SYNC_MODE_SETTING, storeSyncMode, isTabletWritableIn,
  type SettingScope, type FidTier, type SwitchName, type TierDisplay, type StoreSyncMode, type LoyaltyLedgerMode,
} from './settings-catalog';

// La TVA du MAGASIN — son régime (franchise en base), le taux qu'il donne à un article créé sans taux (celui de sa
// famille d'abord) et les taux qu'il propose. Les écrivains d'articles (producteur, reprise, écrans de création) et la
// caisse lisent la même valeur de la même façon.
export {
  VAT_DEFAULT_RATE_SETTING, VAT_FRANCHISE_SETTING, VAT_OFFERED_RATES_SETTING, PLATFORM_DEFAULT_VAT_RATE_BP, LEGAL_VAT_RATES_BP,
  defaultVatRateBp, articleDefaultVatRateBp, offeredVatRatesBp, formatOfferedVatRates, vatSettingValueProblem,
} from './vat-settings';

// Les COORDONNÉES du point de vente — téléphone (E.164) et e-mail de contact. Le siège les saisit, la caisse les lira.
export { STORE_PHONE_SETTING, STORE_EMAIL_SETTING, storeContactValueProblem } from './store-contact';
// Le GLN d'un magasin, à sa déclaration — l'assistant de la Console et son serveur refusent la même chose.
export { storeGlnProblem } from './store-gln';

// Le CODE COMMERÇANT NEPTING de chaque caisse — posé par la Console, refusé à l'écriture par le back-office, envoyé par
// la caisse à l'ouverture de sa session de paiement.
export {
  NEPTING_TERMINALS_SETTING, NEPTING_URL_MAX, isNeptingRegisterKey, normalizeMerchantCode, neptingTerminalProblems,
  parseNeptingTerminals, serializeNeptingTerminals, withNeptingTerminal,
  type NeptingTerminal, type NeptingTerminals, type NeptingTerminalsReading,
} from './nepting-terminals';

// L'IDENTITÉ LÉGALE d'une société — ce qui descend en `company.*`. L'écran de la Console qui la saisit et son serveur
// qui l'enregistre appliquent la même règle.
export {
  isValidSiren, isValidSiret, normalizeNaf, intraVatNumber, companyVatNumber, formatSiren, formatSiret, companyIdentityProblems,
  siretDigits, normalizeSiret, normalizeVatNumber, siretCheck, vatCheck,
  type CompanyIdentityDraft, type CompanyIdentityField, type SiretCheck, type VatCheck,
} from './company-identity';

// Les réglages du WALLET — types de titre, clés, défauts des interrupteurs, et les groupes de
// réglages à valeurs distinctes. Même raison d'être que le catalogue : le balayage du back-office,
// son écran et la caisse lisent la même règle.
export {
  COUPON_TITLE_TYPES, couponTemplateKey,
  DISTINCT_SETTING_GROUPS, distinctSettingConflicts, distinctSettingHolders,
  type CouponTitleType, type DistinctSettingGroup, type DistinctSettingConflict,
} from './wallet-settings';

// Le VOCABULAIRE — quel mot afficher pour un champ, selon le métier et la langue. Même raison d'être
// que le catalogue des réglages : la Console pose les défauts, le back-office et la caisse les affichent,
// et la surcharge du commerçant vit dans la même table `setting`. Un seul résolveur pour les trois.
export {
  VOCABULARY_LANGS, DEFAULT_VOCABULARY_LANG, isVocabularyLang,
  VOCABULARY_FIELDS, VOCABULARY_FIELD_KEYS, isVocabularyFieldKey,
  VOCABULARY_OVERRIDE_PREFIX, VOCABULARY_DEFAULT_PREFIX, vocabularySettingKey, parseVocabularySettingKey, vocabularySettingNames,
  VOCABULARY_TERM_KEY_RE, VOCABULARY_TERM_KEY_MAX_LENGTH, termKeyFromWord,
  platformVocabulary, resolveVocabulary, resolveFieldLabel, isUsableWord, firstUsableWord,
  type VocabularyLang, type VocabularyFieldKey, type Vocabulary, type VocabularyLayer, type VocabularySettingRow, type VocabularySettingName,
} from './vocabulary';

// Le catalogue des DROITS d opérateur — même raison d être que celui des réglages : une règle que le
// back-office et la caisse doivent tous deux connaître EST le contrat (§24.3bis).
export {
  RIGHT_DOMAINS, RIGHT_TEMPLATES, TEMPLATE_SUMMARY, ALL_RIGHTS, REGISTER_ENFORCED, REGISTER_ASKS, APPROVER_TEMPLATES, canApprove,
  MOBILE_PARTNER_LOGIN_RIGHT, MOBILE_PARTNER_TILE_RIGHTS,
  rightDef, isEnforcedByRegister, isRefusedByRegister, templateValue, effectiveRight, resolveRights,
  pruneOverrides, mergeStoredRights, undecidedAccountRights, rightsTally, isRightValue, isTemplateKey,
  parseRightsMode, serializeRightsMode, roleLabelOf, resolveOperatorRights, type OperatorRightsMode, type ResolvedOperatorRights,
  accountTemplateId, accountExceptions,
  type RightValue, type RightMode, type RightDef, type RightDomain, type TemplateKey, type DiscountCeilings,
} from './rights-catalog';

// Les modèles de droits du MAGASIN — ce que le back-office écrit, et d'où il projette chaque compte.
export {
  EDITOR_DISCOUNT_CEILINGS, isEditorTemplateId, editorTemplateLabel, templateDisplayLabel, templateCanApprove,
  resolveStoreTemplate, storeTemplates, pruneExceptions, accountValues, accountRightsMode, followedTemplate,
  discountCeilingsOf, discountCeilingsRefusal,
  type StoreTemplateRecord, type StoreTemplate,
} from './rights-templates';

// La reprise depuis un autre magasin du client — le contrat que le back-office rend et que la Console lit.
export type { TakeoverItem, TakeoverPreviewItem, TakeoverPreview, TakeoverReport, TakeoverLot } from './store-takeover';

// Ce qu'un profil d'appareil a le droit de RECEVOIR, colonne par colonne — même raison d'être que
// les deux catalogues ci-dessus : le serveur doit savoir quoi retirer, l'appareil quoi accueillir.
export { PROFILE_COLUMNS, profileColumns } from './profile-columns';

// L'ARBRE des articles rapides — ce que la caisse dessine d'un niveau. Même raison d'être que les
// catalogues : l'aperçu du studio promet l'écran de la caisse, donc il lit la même règle qu'elle.
export {
  drawnTiles, drawnCount, deepArticleCount, railButtons, startPath, QUICK_SOURCE_KINDS, QUICK_TILE_KINDS, QUICK_POUR_MAX, isSourceKind, opensLevel,
  tileKey, articleKey, sourceLevel, boundSourceLevel, drawnSourceLevel, sourceCount, quickSellableSql,
  QUICK_TILE_COLORS, QUICK_TILE_VISUALS, QUICK_TILE_ICON, QUICK_TILE_DEFAULT_ICON, QUICK_PHOTO_FOCAL_MAX,
  QUICK_PHOTO_ZOOM_MIN, QUICK_PHOTO_ZOOM_MAX, isQuickTileColor, isQuickTileVisual, articleTilePhotos, photoFrame, tileFace, tilePhotoChoice,
  QUICK_PRESET_KINDS, PRESET_SOURCE, PRESET_CHILD_MARK, isPresetKind, presetChildId, isPresetChildId, expandPresets,
  QUICK_PHOTO_SIZES, isQuickPhotoSize, inheritedLook, screenLook, NO_SCREEN_VISUALS, QUICK_SCREEN_DEFAULT_VISUAL, type QuickScreenVisuals, type QuickPhotoSize, type QuickTileLook,
  type QuickTileKind, type QuickTileNode, type QuickSourceKind, type QuickLevelEntry, type QuickTileColor, type QuickTileFace, type QuickPresetKind, type QuickPresetNode, type QuickCatalogPhoto, type QuickTileVisual,
} from './quick-tree';

export function rebuildMutationId(entity: string, rowId: string): string {
  return `rec:${entity}:${rowId}`;
}

/** Modes de reconstruction montante [B4] : `fiscal-only` = canaux `up` append-only (sûr, défaut) ;
 *  `full` = ajoute les canaux `bi`, qui ÉCRASENT l'état serveur (last-write-wins) → cible vide exigée. */
export type RebuildMode = 'fiscal-only' | 'full';

/** Canaux à ré-émettre pour un mode de reconstruction donné (ordre FK respecté : voir REBUILD_ORDER). */
export function rebuildChannels(mode: RebuildMode): string[] {
  return REBUILD_ORDER.filter((c) => {
    const d = channelDirection(c);
    return d === 'up' || (mode === 'full' && d === 'bi');
  });
}

/** ORDRE de reconstruction — impose les dépendances de clés étrangères (CADRAGE-Reconstruction §5.4) :
 *  les fiches référencées d'abord, les pièces ensuite, les satellites après. */
export const REBUILD_ORDER: readonly string[] = [
  'settings', 'clients', 'credit_note',                                  // `bi` (mode `full` seulement)
  // ⚠️ LA CLÉ PUBLIQUE AVANT LES PIÈCES QU'ELLE SIGNE. Aucune contrainte de base ne l'impose — il n'y
  //    a pas de clé étrangère ici — mais une reconstruction interrompue en cours de route doit laisser
  //    un serveur où les pièces déjà reposées sont VÉRIFIABLES. Dans l'autre ordre, la panne laisse
  //    des signatures que personne ne peut contrôler, c'est-à-dire le trou qu'on vient de fermer.
  'fiscal_key',
  'fiscal_piece', 'fiscal_jet', 'fiscal_grand_total', 'fiscal_reprint',  // cœur fiscal
  'abandoned_sale', 'cash_session', 'bank_deposit',                      // anti-fraude + trésorerie
  // ⚠️ LE GRAND LIVRE DES TITRES EST ICI, ET APRÈS `credit_note` : ses mouvements se rattachent à un
  //    bon, et le solde ne se re-dérive qu'une fois le bon connu. Dans l'autre ordre, la dérivation
  //    s'abstient (elle n'invente pas de titre) et le solde resterait celui de l'émission — tous les
  //    bons rendus à leur valeur entière, c'est-à-dire de l'argent reconstruit en trop.
  'stock_movement', 'loyalty_ledger', 'loyalty_card_history', 'credit_note_ledger', // grands livres
  'client_erasure_request',                                             // demandes RGPD reçues au comptoir
  'service', 'payment_reminder',                                         // opérationnel
  // ⚠️ AVANT `held_sale`, APRÈS le reste : un brouillon ne référence aucune autre table (son
  //    fournisseur et sa famille sont du TEXTE, pas des clés — c'est le back-office qui rattache).
  //    Le reconstruire compte : ce sont des articles créés en magasin dont le back-office n'a pas
  //    encore décidé. Les taire ferait disparaître de la file de validation des articles déjà
  //    VENDUS, sans que rien ne le signale.
  'article_draft',
  // ⚠️ EN DERNIER, ET C'EST VOULU : un panier suspendu ne référence rien d'autre (son contenu est du
  //    JSON), et il n'a de sens qu'une fois le reste reposé. Il est reconstruit parce que la caisse
  //    le détient encore — le taire ferait disparaître de l'argent en attente d'un back-office
  //    reconstruit, sans que rien ne le signale.
  'held_sale',
];

// (`ok()` / `fail()` — SEUL point de fabrication de l'enveloppe — viennent aussi de `@orgazone/api-envelope`,
//  ré-exportés en tête de fichier. Les redéfinir ici recréerait la dérive qu'on vient d'éliminer.)
